import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toLocalDateString } from "@/lib/utils";

import {
  DollarSign,
  Users,
  TrendingDown,
  Clock,
  FileText,
  LayoutDashboard,
  Landmark,
  Layers,
  UsersRound,
  Banknote,
  ShoppingCart,
  ShieldCheck,
  CalendarDays,
  BarChart2,
} from "lucide-react";

import type { AuditInvoice, AuditEvent } from "@/components/blocks/audit/project-audit-tab";
import {
  PROJECT_ROLE_LABELS,
  PROJECT_ROLE_DISPLAY_ORDER,
  type ProjectRoleType,
  type ProjectRoleConfig,
  type ProjectRoleAssignment,
  type ProjectNote,
} from "@/types";
import { useAuth } from "@/providers/AuthProvider";
import { appendAuditEntry } from "@/lib/audit-log-storage";
import { authFetch } from "@/lib/auth-fetch";

import { useInvoiceTemplates } from "@/contexts/InvoiceTemplateContext";
import { getProjectBillingConfig, updateProjectBillingConfig } from "@/services/billingService";
import { getNextAutoInvoiceDate } from "@/lib/billing/auto-invoice-schedule";

import { useBudgetPermissions } from "@/hooks/use-budget-permissions";
import { useBudgetData, useUpdateBudget } from "@/hooks/use-budget-data";
import { useResourceBudgetSettings } from "@/hooks/use-resource-budget-settings";
import { enrichBudgetWithAllocatedHours } from "@/lib/budget-team-utils";
import {
  computeAutoCalculatedCompletionPercent,
  computeEffectiveCompletionPercent,
} from "@/lib/completion";
import { useCompany } from "@/contexts/CompanyContext";

import type { BudgetData } from "@/types";
import type { RoleBudget } from "@/types/budget.types";
import type { AssignmentSelection } from "@/types/assignment.types";
import { toast } from "sonner";
import { DEFAULT_COST_CENTRE_COLUMNS, type CostCentre, type CostCentreColumnDefinition } from "@/components/blocks/cost-centres/cost-centres-table";

import { useColumnPreferences } from '@/hooks/use-column-preferences';
import { type ColumnSort } from '@/components/ui/data-table-enhanced';

import { calculatePagination, paginateArray, type PaginationMetadata } from '@/types/pagination';
import { type HierarchicalCostCentre } from "@/components/blocks/cost-centres/cost-centres-hierarchical-table";

import { type TabConfig } from "@/components/tab-settings-dialog";

import { useSchedulerConnection } from "@/hooks/use-scheduler-connection";
import { buildApprovedHoursByEmployee, buildTeamMembers } from "./build-team-members";
import { fetchSchedulingBookings, fetchResourceRequests, fetchRoleCoverage, type ResourceRequestResponse } from "@/services/schedulingService";
import { type ResourceRequest, type SchedulingBooking, type RoleCoverage } from "@/lib/mock-scheduling-data";
import {
  fetchProjectById,
  fetchClients,
  fetchEmployees,
  fetchResourceAllocations,
  fetchCostCentreStructureByProjectId,
  fetchTimeEntriesByProjectId,
  fetchExpensesByProjectId,
  fetchMilestonesByProjectId,
  fetchChangeRequestsByProjectId,
  updateProject,
  getDataMode,
  getApiBaseUrl,
  deleteResourceAllocation,
  createResourceAllocation,
  createPersonBudget,
  fetchResourceBudgetIdForProject,
  fetchColumnPreferences,
  fetchBillingWipItemsPaged,
  fetchBillingInvoices,
  fetchBillingInvoicesPaged,
  refreshInvoicePaymentStatus,
  fetchBillingInvoiceById,
  createBillingDraft,
  deleteBillingDraft,
  postBillingDraftToFinancials,
  voidBillingInvoice,
  mapInvoiceStatus,
  deriveInvoiceDisplayStatus,
  mapWipSourceType,
  fetchProjectNotes,
  createProjectNote,
  saveProjectObjectives,
  saveProjectKeyDeliverables,
  triggerCostingSync,
  getCostingSyncStatus,
  fetchProjectTabOrder,
  saveProjectTabOrder,
  fetchTeamExclusionsByProjectId,
  createTeamExclusion,
} from "@/lib/data-service";
import type { BillingWipItem, BillingInvoice, WipSummary } from "@/lib/data-service";
import type { EditDraftData } from "@/components/billing/InvoiceGenerationWizard";
import { useErrorHandler } from "@/hooks/use-error-handler";


import { useClientPanel } from '@/hooks/use-client-panel';
import { getProjectContentSettings } from '@/lib/project-content-settings-storage';
import {
  fetchCustomFieldDefinitions,
  fetchProjectCustomFieldValues,
  saveProjectCustomFieldValues,
  fetchProjectRoleAssignments,
  saveProjectRoleAssignments,
  fetchProjectRoleConfigs,
  updateCostCentre,
  fetchCostCentreBudgetsByProject,
} from '@/lib/data-service';
import type { CustomFieldDefinitionResponse } from '@/lib/data-service';
import { createBudgetRevision } from '@/lib/data-service';

import type {
  Project,
  Client,
  Employee,
  TimeEntry,
  Expense,
  Milestone,
  ResourceAllocation,
  ChangeRequest,
  CompletionPercentAuditEntry,
} from "@/types";
import { PROJECT_STATUS_LABELS, type ProjectStatusId } from "@/types";
import { mockCompletionAuditData } from "@/lib/mock-data";
import { splitGrossIntoNetAndVat, computeInvoicedTotal, computeInvoicedNetTotal } from "@/lib/billing-summary";
import { DEMO_INVOICES } from "@/lib/demo-invoices";
import { useProjectStatuses } from '@/contexts/ProjectStatusContext';
import { generateAndStoreInvoicePdf, hasStoredPdfBatch, fetchFullCompanyDetails } from '@/services/invoice-pdf-service';
import { uploadInvoicePdfToAdm, storeAdmDocumentId } from '@/services/adm-upload-service';
import { useDocumentApi } from '@/providers/DocumentApiProvider';
import type { InvoicePdfTransaction } from '@/types/invoice-pdf';

import type { TeamMember, InternalTabConfig, ProjectDetailsPageProps } from "../types";
import { DEFAULT_INVOICE_COLUMNS, DEFAULT_WIP_COLUMNS } from "../types";
import { usePermissions } from '@/contexts/PermissionsContext';
import type { ProjectTabKey } from '@/types/roles.types';

// Maps internal tab IDs to the permission key used in ProjectTabPermissions
const TAB_ID_TO_PERMISSION_KEY: Record<string, ProjectTabKey> = {
  'overview': 'overview',
  'financials': 'financials',
  'billing': 'billing',
  'sales-budget': 'salesBudget',
  'cost-budget': 'costBudget',
  'resource-budget': 'resourceBudget',
  'cost-centres': 'costCentres',
  'purchases': 'purchases',
  'time': 'time',
  'expenses': 'expenses',
  'team': 'team',
  'resourcing': 'resourcing',
  'milestones': 'milestones',
  'changes': 'changeRequests',
  'audit': 'audit',
  'reports': 'reports',
};

// Deterministically maps a demo-created string project id (e.g. `demo-169…`)
// to a stable, bounded positive integer. Used only for generating demo
// mock-entity ids so a string id doesn't become NaN (which would produce
// NaN-keyed milestones/expenses/change requests).
function hashProjectIdToSeed(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return (Math.abs(hash) % 100000) + 1;
}

export function useProjectDetails({ projectId: projectIdProp, onBack }: ProjectDetailsPageProps) {
  const { statusLabels, selectableStatuses } = useProjectStatuses();
  const { isTabVisible, isLoading: permissionsLoading } = usePermissions();
  // Shared scheduler connection state — reads from localStorage so toggling
  // in Settings → Integrations is immediately reflected here.
  const { isConnected: schedulerConnected } = useSchedulerConnection();
  // ADM document upload context
  const { getDocumentContext, getAccessToken: getAdmAccessToken } = useDocumentApi();
  // Get projectId from URL params or props (props for backward compatibility)
  const { projectId: projectIdFromUrl } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const projectId = projectIdProp || projectIdFromUrl || "";
  const numericProjectId = projectId ? parseInt(projectId, 10) : 0;
  // Real (live) projects always have purely-numeric ids. Demo-created projects
  // use string ids like `demo-1699999999999`, which parseInt would turn into
  // NaN and break lookups (project detail, budget). Keep the raw string id for
  // those and use the numeric id for genuine numeric ids so both modes work.
  const isNumericProjectId = /^\d+$/.test(projectId);
  const lookupProjectId: number | string = isNumericProjectId ? numericProjectId : projectId;
  // Stable numeric seed for demo mock-entity id generation (never NaN).
  const mockSeedProjectId = isNumericProjectId ? numericProjectId : hashProjectIdToSeed(projectId);
  const { authenticatedUser } = useAuth();

  const { activePanelClientId, openClientPanel, closeClientPanel } = useClientPanel();

  const [loading, setLoading] = useState(true);
  const [isStatusSaving, setIsStatusSaving] = useState(false);
  const [isSavingUnified, setIsSavingUnified] = useState(false);
  const [isSavingKeyPeople, setIsSavingKeyPeople] = useState(false);
  const [isSavingObjectives, setIsSavingObjectives] = useState(false);
  const [isSavingDeliverables, setIsSavingDeliverables] = useState(false);
  const [isSavingAttributes, setIsSavingAttributes] = useState(false);
  const [project, setProject] = useState<Project | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [recentClientIds, setRecentClientIds] = useState<string[]>([]);

  // Client change validation state
  const [clientChangeDialogOpen, setClientChangeDialogOpen] = useState(false);
  const [pendingClientChange, setPendingClientChange] = useState<{ clientId: string; selectedClient: Client } | null>(null);
  const [previousClientForDialog, setPreviousClientForDialog] = useState<Client | null>(null);
  const [clientAuditEvents, setClientAuditEvents] = useState<AuditEvent[]>([]);

  /**
   * Invoice send/resend audit events — populated in-session whenever the user
   * sends or resends an invoice. These flow into the Audit tab via extraEvents.
   *
   * TODO (Engineering): Replace with real events fetched from
   *   GET /api/projects/:projectId/audit?category=invoice&subcategory=send
   * and remove addInvoiceSendAuditEvent in favour of cache invalidation.
   */
  const [invoiceSendAuditEvents, setInvoiceSendAuditEvents] = useState<AuditEvent[]>([]);

  const addInvoiceSendAuditEvent = useCallback((event: AuditEvent) => {
    setInvoiceSendAuditEvents((prev) => [...prev, event]);
  }, []);

  const [projectManager, setProjectManager] = useState<Employee | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  // Employees with access to the selected company — the only valid Project/Account Manager picks.
  const [assignableEmployees, setAssignableEmployees] = useState<Employee[]>([]);
  const [recentProjectManagerIds, setRecentProjectManagerIds] = useState<string[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [resourceAllocations, setResourceAllocations] = useState<ResourceAllocation[]>([]);
  const [allResourceAllocations, setAllResourceAllocations] = useState<ResourceAllocation[]>([]);
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [excludedTeamEmployeeIds, setExcludedTeamEmployeeIds] = useState<Set<number>>(new Set());
  const [localBudgetData, setLocalBudgetData] = useState<BudgetData | null>(null);
  const [isStructureView, setIsStructureView] = useState<boolean>(() => {
    try { return localStorage.getItem('cc-structure-view') === 'true'; } catch { return false; }
  });
  const setIsStructureViewPersisted = (val: boolean): void => {
    try { localStorage.setItem('cc-structure-view', String(val)); } catch { /* ignore */ }
    setIsStructureView(val);
  };
  const [teamColumnSorts, setTeamColumnSorts] = useState<ColumnSort[]>([{ columnId: 'name', direction: 'asc' }]);
  const [teamPage, setTeamPage] = useState(1);
  const [teamPageSize, setTeamPageSize] = useState(25);

  const sortedTeamMembers = useMemo(() => {
    const numericCols = new Set(['allocatedHours', 'actualHours', 'remaining', 'utilisationPercent']);
    return [...teamMembers].sort((a, b) => {
      for (const { columnId, direction } of teamColumnSorts) {
        const aVal = a[columnId as keyof TeamMember];
        const bVal = b[columnId as keyof TeamMember];
        const mult = direction === 'asc' ? 1 : -1;
        if (numericCols.has(columnId)) {
          const diff = (Number(aVal) - Number(bVal)) * mult;
          if (diff !== 0) return diff;
        } else {
          const cmp = String(aVal ?? '').localeCompare(String(bVal ?? '')) * mult;
          if (cmp !== 0) return cmp;
        }
      }
      return 0;
    });
  }, [teamMembers, teamColumnSorts]);

  const teamPagination = useMemo<PaginationMetadata>(
    () => calculatePagination(sortedTeamMembers.length, teamPage, teamPageSize),
    [sortedTeamMembers.length, teamPage, teamPageSize],
  );
  const paginatedTeamMembers = useMemo(
    () => paginateArray(sortedTeamMembers, teamPage, teamPageSize).data,
    [sortedTeamMembers, teamPage, teamPageSize],
  );

  const { handleApiError } = useErrorHandler({ useToast: true });
  const { currentCompany } = useCompany();
  const { templates: invoiceTemplates, getTemplate: getInvoiceTemplate } = useInvoiceTemplates();

  // Budget permissions hook
  const budgetPermissions = useBudgetPermissions();
  const { isBudgetingEnabled, isCostCentreMode } = budgetPermissions;

  // Project Information card — card-level edit mode (replaces global isEditMode)
  const [isProjectInfoEditMode, setIsProjectInfoEditMode] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<Project>>({});

  // Inline header editing — name and description live here, not in the card
  const [isNameEditing, setIsNameEditing] = useState(false);
  const [inlineNameValue, setInlineNameValue] = useState("");
  const [isDescEditing, setIsDescEditing] = useState(false);
  const [inlineDescValue, setInlineDescValue] = useState("");

  // PM confirmation dialog
  const [showPmConfirmDialog, setShowPmConfirmDialog] = useState(false);
  const [pendingPmChange, setPendingPmChange] = useState<{ id: string; employee: Employee } | null>(null);

  // Key People card — card-level edit mode
  const [isKeyPeopleEditMode, setIsKeyPeopleEditMode] = useState(false);
  // Local edit state: maps role → employee id string (or '' for cleared)
  const [keyPeopleEditState, setKeyPeopleEditState] = useState<Partial<Record<ProjectRoleType, string>>>({});
  const [hasKeyPeopleUnsavedChanges, setHasKeyPeopleUnsavedChanges] = useState(false);

  // Key People — role configs loaded from settings (display labels, enabled/required)
  const [roleConfigs, setRoleConfigs] = useState<ProjectRoleConfig[]>([]);

  useEffect(() => {
    const loadRoleConfigs = async () => {
      const mode = getDataMode();
      if (mode !== 'live') return;
      try {
        const apiConfigs = await fetchProjectRoleConfigs();
        if (apiConfigs.length > 0) {
          const merged: ProjectRoleConfig[] = PROJECT_ROLE_DISPLAY_ORDER.map((role) => {
            const fromApi = apiConfigs.find((c: { role: string }) => c.role === role);
            if (fromApi) {
              return {
                role: fromApi.role as ProjectRoleType,
                enabled: fromApi.enabled,
                required: fromApi.required,
                displayLabel: fromApi.displayLabel ?? undefined,
              };
            }
            return { role, enabled: true, required: role === 'project-manager', displayLabel: undefined };
          });
          setRoleConfigs(merged);
        }
      } catch (err) {
        console.error('Failed to load role configs:', err);
      }
    };
    loadRoleConfigs();
  }, []);

  /** Resolve the display label for a role — uses custom label from settings if configured, otherwise falls back to the default. */
  const getRoleLabel = useCallback((role: ProjectRoleType): string => {
    const config = roleConfigs.find((c) => c.role === role);
    if (config?.displayLabel) return config.displayLabel;
    return PROJECT_ROLE_LABELS[role];
  }, [roleConfigs]);

  /** Check if a role is enabled in settings. Defaults to true when configs haven't loaded yet. */
  const isRoleEnabled = useCallback((role: ProjectRoleType): boolean => {
    if (roleConfigs.length === 0) return true;
    const config = roleConfigs.find((c) => c.role === role);
    return config?.enabled ?? true;
  }, [roleConfigs]);

  /** Check if a role is required in settings. */
  const isRoleRequired = useCallback((role: ProjectRoleType): boolean => {
    const config = roleConfigs.find((c) => c.role === role);
    return config?.required ?? (role === 'project-manager');
  }, [roleConfigs]);

  // Project Content cards — Objectives, Key Deliverables, Notes (Workstream E)
  // Each card has independent edit mode so editing one never risks discarding another.
  // Read visibility settings from localStorage (set in Settings → Project Settings → Content Fields)
  // TODO [ENGINEERING — Devin]: Replace getProjectContentSettings() with a context or API-backed hook
  //   once GET /api/company/project-content-settings is live, so all users see the same settings.
  const contentFieldSettings = getProjectContentSettings();

  // Project Attributes card — custom field definitions loaded from API
  const [customFieldDefs, setCustomFieldDefs] = useState<import('@/types').ProjectCustomFieldDefinition[]>([]);

  // Project Attributes card — edit mode state
  const [isAttributesEditMode, setIsAttributesEditMode] = useState(false);
  const [attributesEditState, setAttributesEditState] = useState<Record<string, string>>({});
  const [attributesValidationErrors, setAttributesValidationErrors] = useState<Record<string, string>>({});
  const [isObjectivesEditMode, setIsObjectivesEditMode] = useState(false);
  const [objectivesEditValue, setObjectivesEditValue] = useState("");
  const [isDeliverablesEditMode, setIsDeliverablesEditMode] = useState(false);
  const [deliverablesEditValue, setDeliverablesEditValue] = useState("");
  // Note log — replaces free-text notes field (v1.4 decision: timestamped audit log)
  const [noteLog, setNoteLog] = useState<ProjectNote[]>([]);
  const [newNoteValue, setNewNoteValue] = useState("");
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);

  // Client Information card — card-level edit mode
  const [isClientInfoEditMode, setIsClientInfoEditMode] = useState(false);
  const [clientInfoEditData, setClientInfoEditData] = useState<{ accountManagerId?: number; clientIds?: string }>({});
  const [hasClientInfoUnsavedChanges, setHasClientInfoUnsavedChanges] = useState(false);

  // Account Manager state
  const [accountManager, setAccountManager] = useState<Employee | null>(null);
  const [recentAccountManagerIds, setRecentAccountManagerIds] = useState<string[]>([]);

  // AM confirmation dialog
  const [showAmConfirmDialog, setShowAmConfirmDialog] = useState(false);
  const [pendingAmChange, setPendingAmChange] = useState<{ id: string; employee: Employee } | null>(null);

  // Active tab state — supports deep-linking via the `?tab=` query param
  // (e.g. /projects/123?tab=billing). The requested tab is validated against
  // the available tabs below; an unknown or permission-hidden tab falls back
  // to "overview".
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get("tab") || "overview");
  const [timeTabPrefilterNames, setTimeTabPrefilterNames] = useState<string[]>([]);
  const [timeTabPrefilterBillable, setTimeTabPrefilterBillable] = useState(false);

  // Scheduling data state (Phase 1 — read-only booking data from EBScheduler)
  const [schedulingBookings, setSchedulingBookings] = useState<SchedulingBooking[]>([]);
  const [schedulingLoading, setSchedulingLoading] = useState(false);
  const [schedulingError, setSchedulingError] = useState<string | null>(null);
  const [schedulingConnectionStatus, setSchedulingConnectionStatus] = useState<'connected' | 'not_configured' | 'error'>('not_configured');
  const [schedulingSchedulerName, setSchedulingSchedulerName] = useState<string | null>(null);
  const [schedulerUrl, setSchedulerUrl] = useState<string | null>(null);

  // Resource requests state (fetched from local DB via GET /scheduling/requests)
  const [liveResourceRequests, setLiveResourceRequests] = useState<ResourceRequest[]>([]);
  const [resourceRequestsLoading, setResourceRequestsLoading] = useState(false);

  // Role coverage state (populated by ResourcingTab, consumed by ResourceRequestModal)
  const [liveRoleCoverage, setLiveRoleCoverage] = useState<RoleCoverage[]>([]);

  // WIP Items state (for Unbilled Work tab)
  const [selectedWipItems, setSelectedWipItems] = useState<string[]>([]);
  const [wipSearchQuery, setWipSearchQuery] = useState("");
  const [wipTypeFilter, setWipTypeFilter] = useState("all-types");
  const [wipResourceFilter, setWipResourceFilter] = useState("all-resources");
  const [wipGroupBy, setWipGroupBy] = useState("none");
  const [wipDateFrom, setWipDateFrom] = useState<Date | undefined>(undefined);
  const [wipDateTo, setWipDateTo] = useState<Date | undefined>(undefined);
  const [wipCurrentPage, setWipCurrentPage] = useState(1);
  const [wipItemsPerPage, setWipItemsPerPage] = useState(25);
  const [wipColumnSorts, setWipColumnSorts] = useState<ColumnSort[]>([{ columnId: 'date', direction: 'desc' }]);

  // Billing data state (live mode)
  const [billingWipItems, setBillingWipItems] = useState<BillingWipItem[]>([]);
  const [billingInvoices, setBillingInvoices] = useState<BillingInvoice[]>([]);
  // Billing sub-tab (controlled so it can be switched programmatically after invoice send)
  const [billingSubTab, setBillingSubTab] = useState<string>('invoices');



  const [billingLoading, setBillingLoading] = useState(false);
  const [billingActionLoading, setBillingActionLoading] = useState(false);
  const [pdfExistsSet, setPdfExistsSet] = useState<Set<string>>(new Set());
  const [wipTotalCount, setWipTotalCount] = useState(0);
  const [wipSummary, setWipSummary] = useState<WipSummary>({ totalNetAmount: 0, timeNetAmount: 0, expenseNetAmount: 0, timeHours: 0 });

  const [expensesLoading, setExpensesLoading] = useState(false);
  // True until the background fetch of time entries resolves. Time entries feed
  // the realisation-rate health dimension, so the derived project health is not
  // reliable until this is false — used to avoid flashing a stale "Healthy" badge.
  const [timeEntriesLoading, setTimeEntriesLoading] = useState(true);

  // Cost Centres state
  const [costCentres, setCostCentres] = useState<CostCentre[]>([]);
  // True while cost centres are being fetched — cost centres load lazily after
  // the main project load resolves, so the tab needs its own loading signal.
  const [costCentresLoading, setCostCentresLoading] = useState(true);

  const [hierarchicalCostCentres, setHierarchicalCostCentres] = useState<HierarchicalCostCentre[]>([]);
  const [costCentreColumns, setCostCentreColumns] = useState<CostCentreColumnDefinition[]>(() => {
    const validKeys = new Set(DEFAULT_COST_CENTRE_COLUMNS.map(c => c.key));
    // Initialize from localStorage as immediate fallback
    try {
      const stored = localStorage.getItem('cost-centre-column-preferences');
      if (stored) {
        const parsed = JSON.parse(stored) as CostCentreColumnDefinition[];
        const filtered = parsed.filter(c => validKeys.has(c.key));
        return filtered.length > 0 ? filtered : DEFAULT_COST_CENTRE_COLUMNS;
      }
    } catch {
      // Ignore parse errors
    }
    return DEFAULT_COST_CENTRE_COLUMNS;
  });
  const [costCentreColumnConfigOpen, setCostCentreColumnConfigOpen] = useState(false);

  // Load cost centre column preferences from API in live mode
  useEffect(() => {
    async function loadCostCentreColumnPrefs() {
      const mode = getDataMode();
      if (mode !== 'live') return;
      try {
        const apiColumns = await fetchColumnPreferences('cost-centre-column-preferences');
        if (apiColumns && apiColumns.length > 0) {
          const validKeys = new Set(DEFAULT_COST_CENTRE_COLUMNS.map(c => c.key));
          const filtered = (apiColumns as unknown as CostCentreColumnDefinition[]).filter(c => validKeys.has(c.key));
          if (filtered.length > 0) {
            setCostCentreColumns(filtered);
          }
        }
      } catch (error) {
        console.error('Failed to load cost centre column preferences from API:', error);
      }
    }
    loadCostCentreColumnPrefs();
  }, []);

  // Project Settings state
  const [projectSettings, setProjectSettings] = useState({
    billingFrequency: "monthly",
    invoiceTemplateId: null as string | number | null,
    invoiceEmailTemplateId: null as string | number | null,
    creditNoteEmailTemplateId: null as string | number | null,
    expenseRecharge: "at-cost",
    expenseMarkupPercent: "",
    purchaseRecharge: "at-cost",
    purchaseMarkupPercent: "",
    notToExceedCapEnabled: false,
    notToExceedCapAmount: "",
    // Auto-generate invoice scheduling
    autoGenerateEnabled: false,
    monthlyPattern: "specific-day",
    monthlyDay: "1",
    weeklyDay: "friday",
    // Finance system integration — required for Fixed Fee invoice posting
    defaultCostCentreCode: null as string | null,
    // Billing trigger type — fixed-price projects only
    billingTriggerType: '' as string,
  });

  // Last draft invoice produced by the auto-generation job — anchors fortnightly/quarterly previews
  const [lastAutoGeneratedAt, setLastAutoGeneratedAt] = useState<string | null>(null);

  // Load persisted billing config from the API on mount.
  // Restores ALL billing settings so the drawer shows the correct saved values.
  useEffect(() => {
    if (!numericProjectId) return;
    getProjectBillingConfig(numericProjectId).then((config) => {
      const storedId = config.invoiceTemplateId;

      // Resolve the template id: use the saved value, or fall back to the default template
      let resolvedTemplateId: string | number | null = storedId ?? null;
      if (resolvedTemplateId === null) {
        const defaultTpl = invoiceTemplates.find((t) => t.isDefault) ?? invoiceTemplates[0];
        if (defaultTpl) {
          resolvedTemplateId = defaultTpl.id;
          // Persist the default template so future loads find it
          updateProjectBillingConfig(numericProjectId, { invoiceTemplateId: defaultTpl.id });
        }
      }

      setLastAutoGeneratedAt(config.lastAutoGeneratedAt ?? null);

      // Always restore ALL fields in a single state update
      setProjectSettings((prev) => ({
        ...prev,
        invoiceTemplateId: resolvedTemplateId ?? prev.invoiceTemplateId,
        invoiceEmailTemplateId: config.invoiceEmailTemplateId ?? prev.invoiceEmailTemplateId,
        creditNoteEmailTemplateId: config.creditNoteEmailTemplateId ?? prev.creditNoteEmailTemplateId,
        billingFrequency: config.billingFrequency || prev.billingFrequency,
        autoGenerateEnabled: config.autoGenerate ?? prev.autoGenerateEnabled,
        monthlyPattern: config.monthlyPattern || prev.monthlyPattern,
        monthlyDay: config.monthlyDay || prev.monthlyDay,
        weeklyDay: config.weeklyDay || prev.weeklyDay,
        defaultCostCentreCode: config.defaultCostCentreCode ?? prev.defaultCostCentreCode,
        billingTriggerType: config.billingTriggerType || prev.billingTriggerType,
        expenseRecharge:
          config.expenseRechargeRule === 'none' ? 'not-rechargeable'
          : config.expenseRechargeRule === 'marked-up' ? 'markup'
          : config.expenseRechargeRule || prev.expenseRecharge,
        expenseMarkupPercent:
          config.expenseMarkupPercent != null ? String(config.expenseMarkupPercent) : prev.expenseMarkupPercent,
        purchaseRecharge:
          config.rechargesPurchases === false ? 'not-rechargeable'
          : config.purchaseRechargeRule === 'none' ? 'not-rechargeable'
          : config.purchaseRechargeRule === 'marked-up' ? 'markup'
          : config.purchaseRechargeRule || prev.purchaseRecharge,
        purchaseMarkupPercent:
          config.purchaseMarkupPercent != null ? String(config.purchaseMarkupPercent) : prev.purchaseMarkupPercent,
        notToExceedCapEnabled: config.nteCapAmount != null && config.nteCapAmount > 0,
        notToExceedCapAmount:
          config.nteCapAmount != null ? String(config.nteCapAmount) : prev.notToExceedCapAmount,
      }));

      // Restore completionPercentMode into the project state so the
      // Billing Configuration drawer shows the persisted value.
      if (config.completionPercentMode) {
        setProject((prev) =>
          prev ? { ...prev, completionPercentMode: config.completionPercentMode } : prev
        );
      }
    }).catch((error) => {
      console.error('Failed to load billing config:', error);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericProjectId]);

  const [settingsSaving] = useState(false);
  const [configureStructureOpen, setConfigureStructureOpen] = useState(false);
  const [defaultCostCentreError, setDefaultCostCentreError] = useState(false);
  const [selectedCostCentre, setSelectedCostCentre] = useState<CostCentre | null>(null);
  const [costCentreDetailsPanelOpen, setCostCentreDetailsPanelOpen] = useState(false);
  const [costCentreFullModalOpen, setCostCentreFullModalOpen] = useState(false);
  const [createCostCentrePanelOpen, setCreateCostCentrePanelOpen] = useState(false);

  // Budget state
  const {
    data: budgetData,
    loading: budgetLoading,
    error: budgetError,
    refetch: refetchBudget,
  } = useBudgetData(lookupProjectId);
  const { updateBudget, loading: updateBudgetLoading } = useUpdateBudget();
  const { settings: resourceSettings } = useResourceBudgetSettings();
  const [salesBudgetEditModalOpen, setSalesBudgetEditModalOpen] = useState(false);
  const [editSalesBudgetPageOpen, setEditSalesBudgetPageOpen] = useState(false);
  const [costBudgetEditModalOpen, setCostBudgetEditModalOpen] = useState(false);
  const [editCostBudgetPageOpen, setEditCostBudgetPageOpen] = useState(false);
  const [billingSettingsDrawerOpen, setBillingSettingsDrawerOpen] = useState(false);
  const [drawerEditMode, setDrawerEditMode] = useState(false);
  const [drawerSettings, setDrawerSettings] = useState(projectSettings);
  const [drawerSaving, setDrawerSaving] = useState(false);

  // Keep drawerSettings in sync with projectSettings when the drawer is open
  // but NOT being edited. This ensures that if the billing config API responds
  // after the drawer was opened, the drawer reflects the loaded values.
  useEffect(() => {
    if (billingSettingsDrawerOpen && !drawerEditMode) {
      setDrawerSettings(projectSettings);
    }
  }, [projectSettings, billingSettingsDrawerOpen, drawerEditMode]);
  const [resourceBudgetEditModalOpen, setResourceBudgetEditModalOpen] = useState(false);
  const [editResourceBudgetPageOpen, setEditResourceBudgetPageOpen] = useState(false);
  const [revisionHistoryOpen, setRevisionHistoryOpen] = useState(false);

  // Cost budget revision history state
  const [showCostRevisionHistory, setShowCostRevisionHistory] = useState(false);
  const [costLoadedRevision, setCostLoadedRevision] = useState<import('@/types/budget.types').BudgetRevision | null>(null);
  const [costMakeActiveRevision, setCostMakeActiveRevision] = useState<import('@/types/budget.types').BudgetRevision | null>(null);
  const [costMakeActiveSource, setCostMakeActiveSource] = useState<'restore' | 'make-active'>('make-active');
  const [isCostNewRevision, setIsCostNewRevision] = useState(false);

  // Sales budget revision history state
  const [showSalesRevisionHistory, setShowSalesRevisionHistory] = useState(false);
  const [salesLoadedRevision, setSalesLoadedRevision] = useState<import('@/types/budget.types').BudgetRevision | null>(null);
  const [salesMakeActiveRevision, setSalesMakeActiveRevision] = useState<import('@/types/budget.types').BudgetRevision | null>(null);
  const [salesMakeActiveSource, setSalesMakeActiveSource] = useState<'restore' | 'make-active'>('make-active');
  const [isSalesNewRevision, setIsSalesNewRevision] = useState(false);
  const [viewingRevision, setViewingRevision] = useState<import('@/types/budget.types').BudgetRevision | null>(null);
  const [viewingRevisionPageOpen, setViewingRevisionPageOpen] = useState(false);
  const [newRevisionPageOpen, setNewRevisionPageOpen] = useState(false);
  const [duplicateRevision, setDuplicateRevision] = useState<import('@/types/budget.types').BudgetRevision | null>(null);
  const [makeActiveRevision, setMakeActiveRevision] = useState<import('@/types/budget.types').BudgetRevision | null>(null);
  const [makeActiveSource, setMakeActiveSource] = useState<'restore' | 'make-active'>('make-active');

  const [budgetExportModalOpen, setBudgetExportModalOpen] = useState(false);
  const [activeBudgetType, setActiveBudgetType] = useState<"sales" | "cost" | "resource">("sales");
  // Resource Request Modal (Resource Scheduling integration)
  const [raiseRequestModalOpen, setRaiseRequestModalOpen] = useState(false);
  const [raiseRequestPrefilledRole, setRaiseRequestPrefilledRole] = useState<string | undefined>(undefined);
  // Cost Centre mode only — the cost centre (and its roles) a batched request is raised for
  const [raiseRequestCostCentre, setRaiseRequestCostCentre] = useState<import('@/types/budget.types').CostCentreRequestGroup | undefined>(undefined);
  // Tracks roles that have had a request raised this session (used for context display only)
  const [pendingRequestRoles, setPendingRequestRoles] = useState<Set<string>>(new Set());
  // Counter to trigger re-fetch of resource requests after a new one is created
  const [resourceRequestsRefreshKey, setResourceRequestsRefreshKey] = useState(0);

  const handleRequestSubmitSuccess = (roleName: string | string[]) => {
    const roleNames = Array.isArray(roleName) ? roleName : [roleName];
    setPendingRequestRoles((prev) => {
      const next = new Set(prev);
      roleNames.forEach((name) => next.add(name));
      return next;
    });
    setResourceRequestsRefreshKey((prev) => prev + 1);
    if (!projectId) return;
    fetchRoleCoverage(projectId)
      .then(setLiveRoleCoverage)
      .catch((err) => {
        console.error('[ProjectDetails] Failed to refresh role coverage:', err);
        setSchedulingError('Role coverage could not be refreshed. The displayed values may be out of date.');
      });
  };
  const [invoiceWizardOpen, setInvoiceWizardOpen] = useState(false);
  // Edit-Draft mode — when set, the Invoice Generation Wizard opens pre-populated
  // with this saved draft invoice's lines & values (opened via the Draft "Edit" action).
  const [editDraftData, setEditDraftData] = useState<EditDraftData | null>(null);
  const [editDraftLoading, setEditDraftLoading] = useState(false);
  // Fixed Fee billing — tracks which milestone opened the wizard (Phase 3/4)
  const [fixedFeeMilestone, setFixedFeeMilestone] = useState<{ id: number; name: string; amount: number; dueDate: string } | null>(null);

  // Completion % override editing (Phase 13.2)
  const [completionPercentEditing, setCompletionPercentEditing] = useState(false);
  const [completionPercentInputValue, setCompletionPercentInputValue] = useState<string>('');
  const [evmExpanded, setEvmExpanded] = useState(false);
  const [financialAnalysisExpanded, setFinancialAnalysisExpanded] = useState(false);
  // Audit trail collapsible (Phase 13.4)
  const [completionAuditOpen, setCompletionAuditOpen] = useState(false);
  // Audit tab — pre-set category filter when navigating from the KPI card link
  const [auditCategoryFilter, setAuditCategoryFilter] = useState<string>("all");
  // Completion % audit data — in live mode the API provides real data via the audit-log endpoint;
  // in demo mode fall back to mock data keyed by project ID.
  const completionAuditEntries: CompletionPercentAuditEntry[] = getDataMode() === 'live'
    ? []
    : (project ? (mockCompletionAuditData[project.id] ?? []) : []);

  // Invoice details sheet state
  const [invoiceDetailsSheetOpen, setInvoiceDetailsSheetOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);

  // Create draft dialog state
  const [createDraftDialogOpen, setCreateDraftDialogOpen] = useState(false);
  
  // Invoice column configuration
  const [invoiceColumnConfigOpen, setInvoiceColumnConfigOpen] = useState(false);
  
  // Invoice pagination state
  const [invoiceCurrentPage, setInvoiceCurrentPage] = useState(1);
  const [invoiceItemsPerPage, setInvoiceItemsPerPage] = useState(25);
  const [invoiceTotalCount, setInvoiceTotalCount] = useState(0);

  // Invoice summary totals (across ALL invoices, not just current page)
  const [invoiceTotalInvoiced, setInvoiceTotalInvoiced] = useState(0);
  // Invoiced excluding VAT — compared against the (net) budget in the billing summary.
  const [invoiceTotalInvoicedNet, setInvoiceTotalInvoicedNet] = useState(0);
  const [invoiceTotalOutstanding, setInvoiceTotalOutstanding] = useState(0);
  const [invoiceTotalPaid, setInvoiceTotalPaid] = useState(0);

  // Invoice multi-column sort state
  const [invoiceColumnSorts, setInvoiceColumnSorts] = useState<ColumnSort[]>([]);
  
  // Invoice filtering and search state
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<string>("all");
  const [invoiceSearchQuery, setInvoiceSearchQuery] = useState("");

  // Invoice columns - persisted per user via useColumnPreferences
  const { columns: invoiceColumns, updateColumns: updateInvoiceColumns } = useColumnPreferences({
    storageKey: 'billing-invoice-column-preferences',
    defaultColumns: DEFAULT_INVOICE_COLUMNS,
  });

  // Get visible invoice columns
  const visibleInvoiceColumns = useMemo(
    () => invoiceColumns.filter(col => col.visible).sort((a, b) => a.order - b.order),
    [invoiceColumns]
  );

  // WIP column configuration state
  const [wipColumnConfigOpen, setWipColumnConfigOpen] = useState(false);

  // WIP columns - persisted per user via useColumnPreferences
  const { columns: wipColumns, updateColumns: updateWipColumns } = useColumnPreferences({
    storageKey: 'billing-wip-column-preferences',
    defaultColumns: DEFAULT_WIP_COLUMNS,
  });

  const visibleWipColumns = useMemo(
    () => wipColumns.filter(col => col.visible).sort((a, b) => a.order - b.order),
    [wipColumns]
  );

  // Check if resource budgeting is enabled.
  // In Cost Centre mode all three budget tabs are always shown (PRD §4.4),
  // so bypass the resourceSettings.budgetType guard entirely.
  const isResourceBudgetEnabled =
    budgetPermissions.isCostCentreMode ||
    (resourceSettings.budgetType !== "none" && budgetPermissions.settings.permissions.resourceBudgetEnabled);

  // Tab configuration state
  const [tabSettingsOpen, setTabSettingsOpen] = useState(false);

  // Define all available tabs based on permissions.
  // Return an empty list while permissions are still loading so that
  // `initTabs` (below) does not run against the permissive defaults
  // and accidentally include tabs the user's role has set to Hidden.
  const allAvailableTabs = useMemo<InternalTabConfig[]>(() => {
    if (permissionsLoading) return [];

    const tabs: InternalTabConfig[] = [
      { id: 'overview', label: 'Overview', IconComponent: LayoutDashboard },
      { id: 'financials', label: 'Financials', IconComponent: Landmark },
      { id: 'billing', label: 'Billing', IconComponent: FileText },
    ];

    if (isBudgetingEnabled && budgetPermissions.settings.permissions.salesBudgetEnabled) {
      tabs.push({ id: 'sales-budget', label: 'Sales Budget', IconComponent: DollarSign });
    }
    if (isBudgetingEnabled && budgetPermissions.settings.permissions.costBudgetEnabled) {
      tabs.push({ id: 'cost-budget', label: 'Cost Budget', IconComponent: TrendingDown });
    }
    if (isBudgetingEnabled && isResourceBudgetEnabled) {
      tabs.push({ id: 'resource-budget', label: 'Resource Budget', IconComponent: Users });
    }

    tabs.push(
      { id: 'cost-centres', label: 'Cost Centres', IconComponent: Layers },
      { id: 'purchases', label: 'Purchases', IconComponent: ShoppingCart },
      { id: 'time', label: 'Time', IconComponent: Clock },
      { id: 'expenses', label: 'Expenses', IconComponent: Banknote },
      { id: 'team', label: 'Team', IconComponent: UsersRound },
    );

    if (schedulerConnected) {
      tabs.push({ id: 'resourcing', label: 'Resourcing', IconComponent: CalendarDays });
    }

    tabs.push(
      { id: 'audit', label: 'Audit', IconComponent: ShieldCheck },
      { id: 'reports', label: 'Reports', IconComponent: BarChart2 },
    );

    return tabs.filter(tab => {
      const permKey = TAB_ID_TO_PERMISSION_KEY[tab.id];
      if (!permKey) return true;
      return isTabVisible(permKey);
    });
  }, [permissionsLoading, isBudgetingEnabled, budgetPermissions.settings.permissions, isResourceBudgetEnabled, schedulerConnected, isTabVisible]);

  // Once the available tabs resolve, discard any `?tab=` deep-link value that
  // does not correspond to a visible tab so we never land on an empty tab.
  const hasValidatedTabParamRef = useRef(false);
  useEffect(() => {
    if (hasValidatedTabParamRef.current) return;
    if (allAvailableTabs.length === 0) return;
    hasValidatedTabParamRef.current = true;
    setActiveTab(prev => (allAvailableTabs.some(t => t.id === prev) ? prev : "overview"));
  }, [allAvailableTabs]);

  // Initialize with empty array, will be populated in useEffect
  const [selectedTabIds, setSelectedTabIds] = useState<string[]>([]);
  const hasInitializedTabsRef = useRef(false);
  const currentProjectIdRef = useRef<string | null>(null);
  const rawSavedTabOrderRef = useRef<string[] | null>(null);

  // Initialize selectedTabIds when available tabs change or on mount
  useEffect(() => {
    if (allAvailableTabs.length === 0 || !projectId) return;

    // Reset initialization flag if project changed
    if (currentProjectIdRef.current !== projectId) {
      hasInitializedTabsRef.current = false;
      rawSavedTabOrderRef.current = null;
      currentProjectIdRef.current = projectId;
    }

    const availableTabIds = allAvailableTabs.map(t => t.id);

    // After initial load, reconcile: remove tabs that are no longer
    // available (e.g. permission changed to Hidden) and add any newly
    // available tabs (e.g. resourcing appearing after schedulerConnected).
    if (hasInitializedTabsRef.current) {
      setSelectedTabIds(prev => {
        const cleaned = prev.filter(id => availableTabIds.includes(id));
        const newTabs = availableTabIds.filter(id => id !== 'overview' && !cleaned.includes(id));
        if (cleaned.length === prev.length && newTabs.length === 0) return prev;

        const rawOrder = rawSavedTabOrderRef.current;
        if (rawOrder && rawOrder.length > 0) {
          const orderedKnown = rawOrder.filter(id => availableTabIds.includes(id) && id !== 'overview');
          const missingFromSaved = availableTabIds.filter(id => id !== 'overview' && !orderedKnown.includes(id));
          return [...orderedKnown, ...missingFromSaved];
        }
        return [...cleaned, ...newTabs];
      });
      return;
    }

    const initTabs = async () => {
      let storedTabIds: string[] = [];
      let rawOrder: string[] = [];

      // 1. Try API first
      try {
        const apiTabs = await fetchProjectTabOrder(projectId);
        if (apiTabs && apiTabs.length > 0) {
          rawOrder = apiTabs;
          storedTabIds = apiTabs.filter((id: string) => availableTabIds.includes(id) && id !== 'overview');
        }
      } catch {
        // API failed, fall through to localStorage
      }

      // 2. Fallback to localStorage
      if (storedTabIds.length === 0) {
        try {
          const stored = localStorage.getItem(`project-tabs-order-${projectId}`);
          if (stored) {
            const parsed = JSON.parse(stored);
            rawOrder = parsed;
            storedTabIds = parsed.filter((id: string) => availableTabIds.includes(id) && id !== 'overview');
          }
        } catch {}
      }

      rawSavedTabOrderRef.current = rawOrder;

      // 3. Apply
      if (storedTabIds.length > 0) {
        const missingTabIds = availableTabIds.filter(id => id !== 'overview' && !storedTabIds.includes(id));
        setSelectedTabIds([...storedTabIds.filter(id => availableTabIds.includes(id)), ...missingTabIds]);
      } else {
        const defaultTabs = allAvailableTabs.slice(1).map(t => t.id);
        setSelectedTabIds(defaultTabs);
      }

      hasInitializedTabsRef.current = true;
    };

    initTabs();
  }, [allAvailableTabs, projectId]);

  const saveTabOrder = async (tabIds: string[]) => {
    setSelectedTabIds(tabIds);
    rawSavedTabOrderRef.current = tabIds;
    // Keep localStorage as a fast cache for initial render
    try {
      localStorage.setItem(`project-tabs-order-${projectId}`, JSON.stringify(tabIds));
    } catch {}
    // Persist to backend (fire-and-forget, don't block UI)
    try {
      await saveProjectTabOrder(projectId!, tabIds);
    } catch (error) {
      console.error('Error saving tab order to API:', error);
    }
  };

  // Track available width of the tabs container using a callback ref
  const [tabsContainerWidth, setTabsContainerWidth] = useState(0);
  const tabsContainerCallbackRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setTabsContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    setTabsContainerWidth(el.getBoundingClientRect().width);
  }, []);

  const estimateTabWidth = (label: string) => label.length * 7.5 + 48;

  const SETTINGS_RESERVED = 44;
  const MORE_BUTTON_WIDTH = 84;
  const PINNED_TAB_RESERVED = 100;

  // Calculate visible tabs dynamically based on container width
  const visibleTabs = useMemo(() => {
    // Overview is absent while permissions are still loading, when `allAvailableTabs`
    // is deliberately empty — it must not be spread in as an undefined entry.
    const overviewTab = allAvailableTabs.find(t => t.id === 'overview');
    const allOrdered = [
      ...(overviewTab ? [overviewTab] : []),
      ...selectedTabIds
        .map(id => allAvailableTabs.find(t => t.id === id))
        .filter((tab): tab is InternalTabConfig => tab !== undefined),
    ];

    if (tabsContainerWidth === 0) return allOrdered;

    const availableForTabs = tabsContainerWidth - SETTINGS_RESERVED;
    const totalWidth = allOrdered.reduce((sum, tab) => sum + estimateTabWidth(tab.label), 0);
    if (totalWidth <= availableForTabs) return allOrdered;

    const availableWithMore = availableForTabs - MORE_BUTTON_WIDTH - PINNED_TAB_RESERVED;
    let used = 0;
    let count = 0;
    for (const tab of allOrdered) {
      const w = estimateTabWidth(tab.label);
      if (used + w > availableWithMore) break;
      used += w;
      count++;
    }
    return allOrdered.slice(0, Math.max(1, count));
  }, [allAvailableTabs, selectedTabIds, tabsContainerWidth]);

  // Tracks the last tab the user clicked from the More dropdown
  const [pinnedMoreTab, setPinnedMoreTab] = useState<InternalTabConfig | null>(null);

  // Clear pinned tab if it becomes part of the visible tabs
  useEffect(() => {
    if (pinnedMoreTab && visibleTabs.some(t => t.id === pinnedMoreTab.id)) {
      setPinnedMoreTab(null);
    }
  }, [visibleTabs, pinnedMoreTab]);

  const moreTabs = useMemo(() => {
    const visibleTabIds = visibleTabs.map(t => t.id);
    const remainingTabIds = selectedTabIds.filter(id => !visibleTabIds.includes(id));
    return remainingTabIds
      .map(id => allAvailableTabs.find(t => t.id === id))
      .filter((tab): tab is InternalTabConfig => tab !== undefined);
  }, [allAvailableTabs, visibleTabs, selectedTabIds]);

  // Convert tab data to TabConfig format for the settings dialog
  const tabConfigsForDialog = useMemo<TabConfig[]>(() => {
    return allAvailableTabs.map(tab => ({
      id: tab.id,
      label: tab.label,
      IconComponent: tab.IconComponent,
    }));
  }, [allAvailableTabs]);

  // Monthly sales phasing per cost centre — only relevant in Cost Centre mode,
  // where the cost centre roll-up (not the SalesCategory rows) is the sales budget.
  const [ccSalesBudgetEntries, setCcSalesBudgetEntries] = useState<Record<number, Record<string, number>>>({});
  // biome-ignore lint/correctness/useExhaustiveDependencies: budgetData is intentionally included to trigger re-fetch after save
  useEffect(() => {
    if (!budgetPermissions.isCostCentreMode || !costCentres.length) return;
    let cancelled = false;
    fetchCostCentreBudgetsByProject(numericProjectId, 'sales')
      .then(entries => {
        if (cancelled) return;
        const map: Record<number, Record<string, number>> = {};
        for (const entry of entries) {
          if (!entry.monthlyBudgets) continue;
          map[entry.costCentreId] = typeof entry.monthlyBudgets === 'string'
            ? (JSON.parse(entry.monthlyBudgets) as Record<string, number>)
            : entry.monthlyBudgets;
        }
        setCcSalesBudgetEntries(map);
      })
      .catch(err => {
        console.error('[useProjectDetails] Failed to fetch cost centre sales budgets:', err);
      });
    return () => { cancelled = true; };
  }, [budgetPermissions.isCostCentreMode, costCentres, numericProjectId, budgetData]);

  // In Cost Centre mode the sales budget is owned by the cost centre structure,
  // not by the SalesCategory rows, so roll the cost centres up here — once — and
  // feed every consumer (Sales Budget tab, Billing, Overview, Financials) from it.
  const costCentreSalesRollup = useMemo(() => {
    if (!budgetPermissions.isCostCentreMode || !costCentres.length) return null;
    const categories = costCentres.map(cc => {
      const monthly = ccSalesBudgetEntries[cc.id ?? 0];
      // Monthly phasing is authoritative once entered; before that fall back to
      // the cost centre's own RevenueBudget.
      const budget = monthly
        ? Object.values(monthly).reduce((s, v) => s + (v ?? 0), 0)
        : (cc.revenueBudget ?? 0);
      const actual = cc.actualRevenue ?? 0;
      return {
        id: cc.id ?? 0,
        name: `${cc.code} – ${cc.name}`,
        type: 'labour' as const,
        budget,
        actual,
        remaining: budget - actual,
        percentBilled: budget > 0 ? (actual / budget) * 100 : 0,
        scope: 'project' as const,
      };
    });
    return {
      categories,
      budget: categories.reduce((s, c) => s + c.budget, 0),
      actual: categories.reduce((s, c) => s + c.actual, 0),
    };
  }, [budgetPermissions.isCostCentreMode, costCentres, ccSalesBudgetEntries]);

  // Enrich budget data with allocated hours from team members
  const enrichedBudgetData = useMemo(() => {
    // Use local budget data if available (after assignments), otherwise use fetched data
    const base = localBudgetData || budgetData;
    if (!base) return base;

    let dataToEnrich = base;
    if (costCentreSalesRollup && base.salesBudget) {
      // A cost centre roll-up only covers the project once every transaction
      // carries a mapped cost centre code, so keep the stored header totals when
      // the roll-up is still empty rather than reporting 0.
      const storedTotals = base.salesBudget.totals;
      const budget = costCentreSalesRollup.budget || (storedTotals?.budget ?? 0);
      const actual = costCentreSalesRollup.actual || (storedTotals?.actual ?? 0);
      const totals = {
        budget,
        actual,
        remaining: budget - actual,
        percentBilled: budget > 0 ? (actual / budget) * 100 : 0,
      };
      dataToEnrich = {
        ...base,
        salesBudget: { ...base.salesBudget, categories: costCentreSalesRollup.categories, totals },
        summary: {
          ...base.summary,
          revenue: {
            ...base.summary.revenue,
            budget,
            variance: actual - budget,
            variancePercent: budget > 0 ? ((actual - budget) / budget) * 100 : 0,
            progressPercent: budget > 0 ? (actual / budget) * 100 : 0,
          },
        },
      };
    }

    if (!dataToEnrich.resourceBudget) return dataToEnrich;

    return {
      ...dataToEnrich,
      resourceBudget: enrichBudgetWithAllocatedHours(dataToEnrich.resourceBudget, teamMembers),
    };
  }, [budgetData, localBudgetData, teamMembers, costCentreSalesRollup]);

  // ── Cost / Sales revision lists — always include at least a synthetic "current" entry ──
  const costRevisions = useMemo(() => {
    const real = (enrichedBudgetData?.revisions ?? []).filter(r => (r.budgetLayer ?? 'resource') === 'cost');
    if (real.length > 0) return real;
    const cats = enrichedBudgetData?.costBudget?.categories ?? [];
    if (cats.length === 0 && !enrichedBudgetData?.costBudget) return real;
    const totalBudget = enrichedBudgetData?.costBudget?.totals?.budget ?? 0;
    return [{
      id: -1,
      projectId: project?.id ?? 0,
      revisionNumber: 1,
      date: new Date().toISOString().split('T')[0],
      changedBy: 'system',
      changedByName: 'System',
      reason: 'Current active cost budget',
      status: 'active' as const,
      costBudget: totalBudget,
      salesBudget: 0,
      resourceBudget: 0,
      budgetLayer: 'cost' as const,
      isBaseline: true,
      baselineName: 'Current Budget',
      totalBudgetAtRevision: totalBudget,
      budgetChange: 0,
      costSnapshot: cats.map(c => ({
        categoryId: c.id,
        categoryName: c.name,
        type: c.type,
        budget: c.budget,
      })),
      categoryChanges: [],
    }];
  }, [enrichedBudgetData, project?.id]);

  const salesRevisions = useMemo(() => {
    const real = (enrichedBudgetData?.revisions ?? []).filter(r => (r.budgetLayer ?? 'resource') === 'sales');
    if (real.length > 0) return real;
    const cats = enrichedBudgetData?.salesBudget?.categories ?? [];
    const contractValue = enrichedBudgetData?.salesBudget?.contractValue;
    const totalBudget = enrichedBudgetData?.salesBudget?.totals?.budget ?? contractValue ?? 0;
    if (totalBudget === 0 && cats.length === 0) return real;
    return [{
      id: -2,
      projectId: project?.id ?? 0,
      revisionNumber: 1,
      date: new Date().toISOString().split('T')[0],
      changedBy: 'system',
      changedByName: 'System',
      reason: 'Current active sales budget',
      status: 'active' as const,
      costBudget: 0,
      salesBudget: totalBudget,
      resourceBudget: 0,
      budgetLayer: 'sales' as const,
      isBaseline: true,
      baselineName: 'Current Budget',
      totalBudgetAtRevision: totalBudget,
      budgetChange: 0,
      salesSnapshot: cats.map(c => ({
        categoryId: c.id,
        categoryName: c.name,
        type: c.type as import('@/types/budget.types').CostCategoryType,
        budget: c.budget,
      })),
      categoryChanges: [],
    }];
  }, [enrichedBudgetData, project?.id]);

  // WIP Items data - uses API data in live mode, mock data in demo mode
  const dataMode = getDataMode();

  const wipItems = useMemo(() => {
    if (dataMode === "live") {
      // Live mode: transform API data to match existing table shape (may be empty)
      // Exclude Credit Notes (sourceType 5) and Sales (sourceType 8) — they should not appear in Unbilled Work.
      return billingWipItems
        .filter((item) => item.sourceType !== 5 && item.sourceType !== 8)
        .map((item) => {
          // Time entries keep the timesheet Qty/Rate. Non-Time entries (Journals, VAT Journals,
          // Purchases, etc.) come from Financials with Qty=0/Rate=0 and only carry the value in
          // NetAmount, so default Qty=1 and derive Rate from the value to match the old FinOps WIP.
          const isTime = item.sourceType === 1;
          const qty = isTime ? item.quantity : (item.quantity || 1);
          const rate = isTime ? item.rate : (item.rate || item.netAmount);
          return {
            id: `wip-${item.id}`,
            date: item.date ? item.date.split("T")[0] : "",
            resource: item.employeeName || (item.sourceType === 1 || item.sourceType === 2 ? "Unknown" : "-"),
            task: item.taskName || "",
            description: item.description || "",
            type: mapWipSourceType(item.sourceType),
            quantity: qty,
            unit: item.unit || (isTime ? "hrs" : "units"),
            rate: rate,
            amount: item.netAmount,
            costCentre: item.externalCostCentreCode || "",
            reference: `WIP-${item.id}`,
            costRate: item.costRate || 0,
            billingId: item.id, // preserve original ID for API calls
            role: item.roleName || '',
          };
        });
    }

    // Demo mode: use mock data
    return [
      {
        id: "wip-1",
        date: "2024-01-15",
        resource: "John Smith",
        task: "Backend Development",
        description: "API endpoint implementation",
        type: "Time",
        quantity: 8.0,
        unit: "hrs",
        rate: 150,
        amount: 1200,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-2",
        date: "2024-01-16",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Dashboard mockups and prototypes",
        type: "Time",
        quantity: 6.5,
        unit: "hrs",
        rate: 125,
        amount: 812.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-3",
        date: "2024-01-17",
        resource: "Mike Wilson",
        task: "Testing",
        description: "QA and bug fixes",
        type: "Time",
        quantity: 5.0,
        unit: "hrs",
        rate: 100,
        amount: 500,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-4",
        date: "2024-01-18",
        resource: "John Smith",
        task: "Travel",
        description: "Client meeting - London",
        type: "Expense",
        quantity: 1,
        unit: "trip",
        rate: 245.5,
        amount: 245.5,
        costCentre: "Travel",
        reference: "EXP-123",
        costRate: 245.5,
      },
      {
        id: "wip-5",
        date: "2024-01-19",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Component library setup",
        type: "Time",
        quantity: 7.5,
        unit: "hrs",
        rate: 125,
        amount: 937.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-6",
        date: "2024-01-20",
        resource: "Mike Wilson",
        task: "Software",
        description: "Development tools license",
        type: "Expense",
        quantity: 1,
        unit: "item",
        rate: 299,
        amount: 299,
        costCentre: "IT",
        reference: "EXP-124",
        costRate: 299,
      },
      {
        id: "wip-7",
        date: "2024-01-21",
        resource: "Procurement",
        task: "Hardware",
        description: "Dell Laptop - Developer workstation",
        type: "Purchase",
        quantity: 2,
        unit: "units",
        rate: 1500,
        amount: 3000,
        costCentre: "IT",
        reference: "PO-001",
        costRate: 1200,
      },
      {
        id: "wip-8",
        date: "2024-01-22",
        resource: "Procurement",
        task: "Office Supplies",
        description: "Ergonomic chairs for team",
        type: "Purchase",
        quantity: 5,
        unit: "units",
        rate: 450,
        amount: 2250,
        costCentre: "Office",
        reference: "PO-002",
        costRate: 350,
      },
      {
        id: "wip-9",
        date: "2024-01-23",
        resource: "John Smith",
        task: "Backend Development",
        description: "Database optimization",
        type: "Time",
        quantity: 6.0,
        unit: "hrs",
        rate: 150,
        amount: 900,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-10",
        date: "2024-01-24",
        resource: "Procurement",
        task: "Software Licenses",
        description: "Adobe Creative Cloud - Annual subscription",
        type: "Purchase",
        quantity: 3,
        unit: "licenses",
        rate: 599,
        amount: 1797,
        costCentre: "Design",
        reference: "PO-003",
        costRate: 599,
      },
      {
        id: "wip-11",
        date: "2024-01-25",
        resource: "John Smith",
        task: "Backend Development",
        description: "Authentication module implementation",
        type: "Time",
        quantity: 7.5,
        unit: "hrs",
        rate: 150,
        amount: 1125,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-12",
        date: "2024-01-26",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "User flow diagrams",
        type: "Time",
        quantity: 5.0,
        unit: "hrs",
        rate: 125,
        amount: 625,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-13",
        date: "2024-01-27",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Integration testing",
        type: "Time",
        quantity: 8.0,
        unit: "hrs",
        rate: 100,
        amount: 800,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-14",
        date: "2024-01-28",
        resource: "John Smith",
        task: "Backend Development",
        description: "Payment gateway integration",
        type: "Time",
        quantity: 6.5,
        unit: "hrs",
        rate: 150,
        amount: 975,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-15",
        date: "2024-01-29",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Responsive design implementation",
        type: "Time",
        quantity: 7.0,
        unit: "hrs",
        rate: 125,
        amount: 875,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-16",
        date: "2024-01-30",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Performance testing",
        type: "Time",
        quantity: 6.0,
        unit: "hrs",
        rate: 100,
        amount: 600,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-17",
        date: "2024-01-31",
        resource: "John Smith",
        task: "Backend Development",
        description: "Email notification system",
        type: "Time",
        quantity: 5.5,
        unit: "hrs",
        rate: 150,
        amount: 825,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-18",
        date: "2024-02-01",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Icon design and asset creation",
        type: "Time",
        quantity: 4.5,
        unit: "hrs",
        rate: 125,
        amount: 562.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-19",
        date: "2024-02-02",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Security audit and penetration testing",
        type: "Time",
        quantity: 8.0,
        unit: "hrs",
        rate: 100,
        amount: 800,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-20",
        date: "2024-02-03",
        resource: "John Smith",
        task: "Backend Development",
        description: "Data migration scripts",
        type: "Time",
        quantity: 7.0,
        unit: "hrs",
        rate: 150,
        amount: 1050,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-21",
        date: "2024-02-04",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Animation and transitions",
        type: "Time",
        quantity: 6.0,
        unit: "hrs",
        rate: 125,
        amount: 750,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-22",
        date: "2024-02-05",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Cross-browser compatibility testing",
        type: "Time",
        quantity: 5.5,
        unit: "hrs",
        rate: 100,
        amount: 550,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-23",
        date: "2024-02-06",
        resource: "John Smith",
        task: "Backend Development",
        description: "Caching layer implementation",
        type: "Time",
        quantity: 6.5,
        unit: "hrs",
        rate: 150,
        amount: 975,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-24",
        date: "2024-02-07",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Accessibility improvements",
        type: "Time",
        quantity: 5.0,
        unit: "hrs",
        rate: 125,
        amount: 625,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-25",
        date: "2024-02-08",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Load testing and optimization",
        type: "Time",
        quantity: 7.5,
        unit: "hrs",
        rate: 100,
        amount: 750,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-26",
        date: "2024-02-09",
        resource: "John Smith",
        task: "Travel",
        description: "Client presentation - Manchester",
        type: "Expense",
        quantity: 1,
        unit: "trip",
        rate: 185.75,
        amount: 185.75,
        costCentre: "Travel",
        reference: "EXP-125",
        costRate: 185.75,
      },
      {
        id: "wip-27",
        date: "2024-02-10",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Style guide documentation",
        type: "Time",
        quantity: 4.0,
        unit: "hrs",
        rate: 125,
        amount: 500,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-28",
        date: "2024-02-11",
        resource: "Mike Wilson",
        task: "Testing",
        description: "User acceptance testing coordination",
        type: "Time",
        quantity: 6.5,
        unit: "hrs",
        rate: 100,
        amount: 650,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-29",
        date: "2024-02-12",
        resource: "John Smith",
        task: "Backend Development",
        description: "Logging and monitoring setup",
        type: "Time",
        quantity: 5.5,
        unit: "hrs",
        rate: 150,
        amount: 825,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-30",
        date: "2024-02-13",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Error state designs",
        type: "Time",
        quantity: 3.5,
        unit: "hrs",
        rate: 125,
        amount: 437.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-31",
        date: "2024-02-14",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Regression testing",
        type: "Time",
        quantity: 8.0,
        unit: "hrs",
        rate: 100,
        amount: 800,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-32",
        date: "2024-02-15",
        resource: "John Smith",
        task: "Backend Development",
        description: "API documentation",
        type: "Time",
        quantity: 4.5,
        unit: "hrs",
        rate: 150,
        amount: 675,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-33",
        date: "2024-02-16",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Loading state animations",
        type: "Time",
        quantity: 5.5,
        unit: "hrs",
        rate: 125,
        amount: 687.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-34",
        date: "2024-02-17",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Mobile device testing",
        type: "Time",
        quantity: 7.0,
        unit: "hrs",
        rate: 100,
        amount: 700,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-35",
        date: "2024-02-18",
        resource: "John Smith",
        task: "Backend Development",
        description: "Deployment pipeline setup",
        type: "Time",
        quantity: 6.0,
        unit: "hrs",
        rate: 150,
        amount: 900,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-36",
        date: "2024-02-19",
        resource: "Procurement",
        task: "Cloud Services",
        description: "AWS hosting - Monthly subscription",
        type: "Purchase",
        quantity: 1,
        unit: "month",
        rate: 450,
        amount: 450,
        costCentre: "IT",
        reference: "PO-004",
        costRate: 450,
      },
      {
        id: "wip-37",
        date: "2024-02-20",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Onboarding flow design",
        type: "Time",
        quantity: 6.5,
        unit: "hrs",
        rate: 125,
        amount: 812.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-38",
        date: "2024-02-21",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Final QA review",
        type: "Time",
        quantity: 8.0,
        unit: "hrs",
        rate: 100,
        amount: 800,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-39",
        date: "2024-02-22",
        resource: "John Smith",
        task: "Backend Development",
        description: "Production deployment",
        type: "Time",
        quantity: 5.0,
        unit: "hrs",
        rate: 150,
        amount: 750,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-40",
        date: "2024-02-23",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Post-launch UI refinements",
        type: "Time",
        quantity: 4.5,
        unit: "hrs",
        rate: 125,
        amount: 562.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
    ];
  }, [dataMode, billingWipItems]);

  // Filter and sort WIP items based on search, filters, and date range
  const filteredAndSortedWipItems = useMemo(() => {
    let filtered = wipItems.filter((item) => {
      const matchesSearch =
        wipSearchQuery === "" ||
        item.description.toLowerCase().includes(wipSearchQuery.toLowerCase()) ||
        item.task.toLowerCase().includes(wipSearchQuery.toLowerCase()) ||
        item.resource.toLowerCase().includes(wipSearchQuery.toLowerCase());

      const matchesType = wipTypeFilter === "all-types" || item.type.toLowerCase() === wipTypeFilter.toLowerCase();

      const matchesResource =
        wipResourceFilter === "all-resources" ||
        item.resource.toLowerCase().includes(wipResourceFilter.toLowerCase());

      // Date range filter — compare date portion only so From/To are inclusive
      const itemDate = new Date(item.date);
      const itemDateOnly = new Date(itemDate.getFullYear(), itemDate.getMonth(), itemDate.getDate());
      const matchesDateFrom = !wipDateFrom || itemDateOnly >= wipDateFrom;
      const matchesDateTo = !wipDateTo || itemDateOnly <= wipDateTo;

      return matchesSearch && matchesType && matchesResource && matchesDateFrom && matchesDateTo;
    });

    // Sort items using wipColumnSorts (multi-column)
    filtered.sort((a, b) => {
      for (const sort of wipColumnSorts) {
        let aValue: any = a[sort.columnId as keyof typeof a];
        let bValue: any = b[sort.columnId as keyof typeof b];

        // Handle date sorting
        if (sort.columnId === "date") {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }

        if (typeof aValue === "number" && typeof bValue === "number") {
          const cmp = aValue - bValue;
          if (cmp !== 0) return sort.direction === "asc" ? cmp : -cmp;
        } else {
          const aStr = String(aValue ?? "").toLowerCase();
          const bStr = String(bValue ?? "").toLowerCase();
          const cmp = aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
          if (cmp !== 0) return sort.direction === "asc" ? cmp : -cmp;
        }
      }
      return 0;
    });

    return filtered;
  }, [wipItems, wipSearchQuery, wipTypeFilter, wipResourceFilter, wipDateFrom, wipDateTo, wipColumnSorts]);

  // Group WIP items if grouping is enabled
  const groupedWipItems = useMemo(() => {
    if (wipGroupBy === "none") {
      return null;
    }

    const groups: Record<string, typeof filteredAndSortedWipItems> = {};

    filteredAndSortedWipItems.forEach((item) => {
      let groupKey = "";
      if (wipGroupBy === "resource") {
        groupKey = item.resource;
      } else if (wipGroupBy === "costCentre") {
        groupKey = item.costCentre || "Unallocated";
      } else if (wipGroupBy === "date") {
        groupKey = new Date(item.date).toLocaleDateString("en-GB", { 
          year: "numeric", 
          month: "long", 
          day: "numeric" 
        });
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
    });

    return groups;
  }, [filteredAndSortedWipItems, wipGroupBy]);

  // Check whether any client-side filters are active (search, type, resource, date range)
  const hasActiveWipFilters = wipSearchQuery !== "" || wipTypeFilter !== "all-types" || wipResourceFilter !== "all-resources" || wipDateFrom !== undefined || wipDateTo !== undefined;

  // Paginate WIP items
  // In live mode without client-side filters, the server already returns one page.
  // When client-side filters are active (or in demo mode), slice locally.
  const paginatedWipItems = useMemo(() => {
    if (dataMode === "live" && !hasActiveWipFilters) {
      return filteredAndSortedWipItems;
    }
    const startIndex = (wipCurrentPage - 1) * wipItemsPerPage;
    const endIndex = startIndex + wipItemsPerPage;
    return filteredAndSortedWipItems.slice(startIndex, endIndex);
  }, [filteredAndSortedWipItems, wipCurrentPage, wipItemsPerPage, dataMode, hasActiveWipFilters]);

  // Calculate pagination info
  // In live mode without client-side filters use server total; otherwise use local filtered count
  const wipTotalItems = (dataMode === "live" && !hasActiveWipFilters) ? wipTotalCount : filteredAndSortedWipItems.length;
  const wipTotalPages = Math.ceil(wipTotalItems / wipItemsPerPage);
  const wipStartItem = wipTotalItems === 0 ? 0 : (wipCurrentPage - 1) * wipItemsPerPage + 1;
  const wipEndItem = Math.min(wipCurrentPage * wipItemsPerPage, wipTotalItems);

  // Calculate selected totals
  const selectedWipTotals = useMemo(() => {
    const selectedItems = filteredAndSortedWipItems.filter((item) => selectedWipItems.includes(item.id));

    const hours = selectedItems
      .filter((item) => item.type === "Time")
      .reduce((sum, item) => sum + item.quantity, 0);

    const expenseCount = selectedItems.filter((item) => item.type === "Expense").length;

    const amount = selectedItems.reduce((sum, item) => sum + item.amount, 0);

    return {
      count: selectedItems.length,
      hours,
      expenseCount,
      amount,
    };
  }, [filteredAndSortedWipItems, selectedWipItems]);

  // Handle select/deselect individual item
  const handleSelectWipItem = (itemId: string) => {
    setSelectedWipItems((prev) => (prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]));
  };

  // Handle select/deselect all filtered items
  const handleSelectAllWipItems = () => {
    // Get all filtered WIP item IDs (across all pages)
    const allWipIds = filteredAndSortedWipItems.map((item) => item.id);
    
    if (selectedWipItems.length === allWipIds.length && allWipIds.length > 0) {
      // If all items are selected, deselect all
      setSelectedWipItems([]);
    } else {
      // Select all items across all pages
      setSelectedWipItems(allWipIds);
    }
  };

  // Handle page change - fetch new page from server
  const handleWipPageChange = async (newPage: number) => {
    setWipCurrentPage(newPage);
    if (dataMode === "live" && !hasActiveWipFilters) {
      try {
        setBillingLoading(true);
        const response = await fetchBillingWipItemsPaged(numericProjectId, 0, newPage, wipItemsPerPage);
        setBillingWipItems(response.items);
        setWipTotalCount(response.totalCount);
        if (response.summary) setWipSummary(response.summary);
      } catch (error) {
        console.error("Failed to fetch WIP page:", error);
      } finally {
        setBillingLoading(false);
      }
    }
  };

  // Handle page size change - reset to page 1 and re-fetch from server
  const handleWipPageSizeChange = async (newPageSize: number) => {
    setWipItemsPerPage(newPageSize);
    setWipCurrentPage(1);
    if (dataMode === "live") {
      try {
        setBillingLoading(true);
        const response = await fetchBillingWipItemsPaged(numericProjectId, 0, 1, newPageSize);
        setBillingWipItems(response.items);
        setWipTotalCount(response.totalCount);
        if (response.summary) setWipSummary(response.summary);
      } catch (error) {
        console.error("Failed to fetch WIP page with new size:", error);
      } finally {
        setBillingLoading(false);
      }
    }
  };

  const handleRefreshWip = async () => {
    if (dataMode !== "live") return;
    try {
      setBillingLoading(true);
      setSelectedWipItems([]);

      // Trigger costing sync for T&M and fixed-price projects with a code
      const projectType = String(project?.type ?? '').toLowerCase();
      if (project && project.code && currentCompany &&
          (projectType === "time-and-materials" || projectType === "fixed-price")) {
        try {
          await triggerCostingSync(
            project.code,
            numericProjectId,
            currentCompany.id,
            undefined,
            currentCompany.id
          );

          // Poll costing-sync-status until completed or failed (max ~30s)
          const maxPolls = 30;
          for (let i = 0; i < maxPolls; i++) {
            await new Promise(r => setTimeout(r, 1000));
            const syncStatus = await getCostingSyncStatus(numericProjectId);
            if (syncStatus.status === "completed" || syncStatus.status === "failed") {
              if (syncStatus.status === "completed") {
                const match = syncStatus.message?.match(/(\d+)/);
                const count = match ? parseInt(match[1], 10) : 0;
                if (count > 0) {
                  toast.success(`Synced ${count} new transactions`);
                } else {
                  toast.info("No new transactions found");
                }
              } else {
                toast.error(`Sync failed: ${syncStatus.message || "Unknown error"}`);
              }
              break;
            }
          }
        } catch (syncError) {
          console.error("Costing sync failed:", syncError);
          toast.error("Failed to sync costing transactions");
        }
      }

      // Re-fetch budget data so completion % reflects synced hours
      if (projectType === "fixed-price") {
        refetchBudget();
      }

      // Re-fetch WIP items
      const response = await fetchBillingWipItemsPaged(numericProjectId, 0, wipCurrentPage, wipItemsPerPage);
      setBillingWipItems(response.items);
      setWipTotalCount(response.totalCount);
      if (response.summary) setWipSummary(response.summary);
    } catch (error) {
      console.error("Failed to refresh WIP data:", error);
    } finally {
      setBillingLoading(false);
    }
  };

  const handleInvoicePageChange = (newPage: number) => {
    setInvoiceCurrentPage(newPage);
  };

  // Helper functions to format display values to match dropdown labels
  const formatProjectType = (type: string): string => {
    const typeMap: Record<string, string> = {
      "fixed-price": "Fixed Price",
      "time-and-materials": "Time & Materials",
      retainer: "Retainer",
    };
    return typeMap[type] || type;
  };

  const formatRevenueRecognition = (method: string): string => {
    const methodMap: Record<string, string> = {
      "percentage-of-completion": "Percentage of Completion",
      "completed-contract": "Completed Contract",
      "cash-basis": "Cash Basis",
    };
    return methodMap[method] || method;
  };

  // Edit mode handlers
  const handleEnterEditMode = useCallback(() => {
    if (project) {
      setEditFormData({
        statusId: project.statusId,
        type: project.type,
        revenueRecognitionMethod: project.revenueRecognitionMethod,
        startDate: project.startDate,
        endDate: project.endDate,
        projectManagerId: project.projectManagerId,
        billable: project.billable ?? true,
      });
      setIsProjectInfoEditMode(true);
      setHasUnsavedChanges(false);
    }
  }, [project]);

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      setShowCancelDialog(true);
    } else {
      setIsProjectInfoEditMode(false);
      setEditFormData({});
    }
  };

  const handleConfirmCancel = () => {
    setIsProjectInfoEditMode(false);
    setEditFormData({});
    setHasUnsavedChanges(false);
    setShowCancelDialog(false);
    toast.info("Changes discarded");
  };

  // ── Unified card edit mode (Project Info + Client Info together) ──

  const handleEnterUnifiedEditMode = useCallback(() => {
    if (!project) return;
    // Enter Project Info edit mode
    setEditFormData({
      statusId: project.statusId,
      type: project.type,
      revenueRecognitionMethod: project.revenueRecognitionMethod,
      startDate: project.startDate,
      endDate: project.endDate,
      projectManagerId: project.projectManagerId,
      billable: project.billable ?? true,
    });
    setIsProjectInfoEditMode(true);
    setHasUnsavedChanges(false);
    // Enter Client Info edit mode
    setClientInfoEditData({ accountManagerId: project.accountManagerId });
    setIsClientInfoEditMode(true);
    setHasClientInfoUnsavedChanges(false);
  }, [project]);

  const handleCancelUnifiedEdit = () => {
    const anyChanges = hasUnsavedChanges || hasClientInfoUnsavedChanges;
    if (anyChanges) {
      setShowCancelDialog(true);
    } else {
      // No changes — close both
      setIsProjectInfoEditMode(false);
      setEditFormData({});
      setIsClientInfoEditMode(false);
      setClientInfoEditData({});
      setHasClientInfoUnsavedChanges(false);
      setPendingAmChange(null);
      setShowAmConfirmDialog(false);
    }
  };

  const handleConfirmCancelUnified = () => {
    setIsProjectInfoEditMode(false);
    setEditFormData({});
    setHasUnsavedChanges(false);
    setIsClientInfoEditMode(false);
    setClientInfoEditData({});
    setHasClientInfoUnsavedChanges(false);
    setShowCancelDialog(false);
    setPendingAmChange(null);
    setShowAmConfirmDialog(false);
    toast.info("Changes discarded");
  };

  const handleSaveUnifiedChanges = async () => {
    if (isSavingUnified) return;
    try {
      if (!project) return;

      if (project.type === "time-and-materials" && !validateSettings()) {
        return;
      }

      setIsSavingUnified(true);

      const updateData = {
        id: numericProjectId,
        name: project.name,
        code: project.code,
        description: project.description,
        statusId: editFormData.statusId ?? project.statusId,
        type: editFormData.type ?? project.type,
        revenueRecognitionMethod: editFormData.revenueRecognitionMethod ?? project.revenueRecognitionMethod,
        startDate: editFormData.startDate ?? project.startDate,
        endDate: editFormData.endDate ?? project.endDate,
        clientIds: clientInfoEditData.clientIds ?? editFormData.clientIds ?? project.clientIds,
        projectManagerId: editFormData.projectManagerId ?? project.projectManagerId,
        health: project.health,
        billable: editFormData.billable ?? project.billable ?? true,
        accountManagerId: clientInfoEditData.accountManagerId ?? project.accountManagerId,
      };

      await updateProject(numericProjectId, updateData);

      // Cascade billable=false to all cost centres when requested
      if (editFormData.cascadeBillableToCostCentres && editFormData.billable === false) {
        const cascadePromises = costCentres
          .filter((cc) => cc.billable !== false)
          .map((cc) =>
            updateCostCentre(cc.id, {
              id: cc.id,
              projectId: numericProjectId,
              code: cc.code,
              name: cc.name,
              type: cc.type,
              status: cc.status,
              billable: false,
              owner: cc.owner,
              description: cc.description,
              costsBudget: cc.costsBudget,
              actualCosts: cc.actualCosts,
              committedCosts: cc.committedCosts,
              revenueBudget: cc.revenueBudget,
              actualRevenue: cc.actualRevenue,
              hoursBudget: cc.hoursBudget,
              actualHours: cc.actualHours,
              isActive: cc.isActive,
            })
          );
        await Promise.all(cascadePromises);
        setCostCentres((prev) => prev.map((cc) => ({ ...cc, billable: false })));
      }

      if (project.type === "time-and-materials") {
        // TODO: Call API to save settings when backend is ready
      }

      const updatedProject = {
        ...project,
        ...editFormData,
        accountManagerId: clientInfoEditData.accountManagerId ?? project.accountManagerId,
        clientIds: clientInfoEditData.clientIds ?? project.clientIds,
      };
      setProject(updatedProject);

      // Reset both edit modes
      setIsProjectInfoEditMode(false);
      setEditFormData({});
      setHasUnsavedChanges(false);
      setIsClientInfoEditMode(false);
      setClientInfoEditData({});
      setHasClientInfoUnsavedChanges(false);

      toast.success("Project updated successfully");
      window.dispatchEvent(
        new CustomEvent("project-updated", {
          detail: { projectId, project: updatedProject },
        }),
      );
    } catch (error) {
      toast.error("Failed to update project");
      console.error("Error saving project:", error);
    } finally {
      setIsSavingUnified(false);
    }
  };

  const hasUnifiedUnsavedChanges = hasUnsavedChanges || hasClientInfoUnsavedChanges;

  // Inline header — Project Name
  const handleNameEditStart = useCallback(() => {
    if (!project) return;
    setInlineNameValue(project.name);
    setIsNameEditing(true);
  }, [project]);

  const handleNameSave = useCallback(async () => {
    if (!project) return;
    const trimmed = inlineNameValue.trim();
    if (trimmed === project.name) {
      setIsNameEditing(false);
      return;
    }
    if (trimmed.length < 3 || trimmed.length > 100) {
      // Keep editing — validation prevents save
      return;
    }
    try {
      await updateProject(numericProjectId, {
        id: numericProjectId,
        name: trimmed,
        code: project.code,
        description: project.description,
        statusId: project.statusId,
        type: project.type,
        revenueRecognitionMethod: project.revenueRecognitionMethod,
        startDate: project.startDate,
        endDate: project.endDate,
        clientIds: project.clientIds,
        projectManagerId: project.projectManagerId,
        health: project.health,
      });
      const updatedProject = { ...project, name: trimmed };
      setProject(updatedProject);
      setIsNameEditing(false);
      toast.success("Project name updated");
      window.dispatchEvent(new CustomEvent("project-updated", { detail: { projectId, project: updatedProject } }));
    } catch {
      toast.error("Failed to update project name");
    }
  }, [project, inlineNameValue, numericProjectId, projectId]);

  const handleNameKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); void handleNameSave(); }
    if (e.key === 'Escape') { setIsNameEditing(false); }
  }, [handleNameSave]);

  // Inline header — Description
  const handleDescEditStart = useCallback(() => {
    if (!project) return;
    setInlineDescValue(project.description || "");
    setIsDescEditing(true);
  }, [project]);

  const handleDescSave = useCallback(async () => {
    if (!project) return;
    const trimmed = inlineDescValue.trim();
    if (trimmed === (project.description || "").trim()) {
      setIsDescEditing(false);
      return;
    }
    try {
      await updateProject(numericProjectId, {
        id: numericProjectId,
        name: project.name,
        code: project.code,
        description: trimmed,
        statusId: project.statusId,
        type: project.type,
        revenueRecognitionMethod: project.revenueRecognitionMethod,
        startDate: project.startDate,
        endDate: project.endDate,
        clientIds: project.clientIds,
        projectManagerId: project.projectManagerId,
        health: project.health,
      });
      const updatedProject = { ...project, description: trimmed };
      setProject(updatedProject);
      setIsDescEditing(false);
      toast.success("Description updated");
      window.dispatchEvent(new CustomEvent("project-updated", { detail: { projectId, project: updatedProject } }));
    } catch {
      toast.error("Failed to update description");
    }
  }, [project, inlineDescValue, numericProjectId, projectId]);

  const handleDescKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') { setIsDescEditing(false); }
    // Ctrl+Enter or Cmd+Enter saves
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); void handleDescSave(); }
  }, [handleDescSave]);

  const handleSaveChanges = async () => {
    try {
      if (!project) return;

      // Validate settings if on Time & Material project
      if (project.type === "time-and-materials" && !validateSettings()) {
        return;
      }

      const updateData = {
        id: numericProjectId,
        name: project.name,
        code: project.code,
        description: project.description,
        statusId: editFormData.statusId ?? project.statusId,
        type: editFormData.type ?? project.type,
        revenueRecognitionMethod: editFormData.revenueRecognitionMethod ?? project.revenueRecognitionMethod,
        startDate: editFormData.startDate ?? project.startDate,
        endDate: editFormData.endDate ?? project.endDate,
        clientIds: editFormData.clientIds ?? project.clientIds,
        projectManagerId: editFormData.projectManagerId ?? project.projectManagerId,
        health: project.health,
        billable: editFormData.billable ?? project.billable ?? true,
      };

      await updateProject(numericProjectId, updateData);

      // Cascade billable=false to all cost centres when requested
      if (editFormData.cascadeBillableToCostCentres && editFormData.billable === false) {
        const cascadePromises = costCentres
          .filter((cc) => cc.billable !== false)
          .map((cc) =>
            updateCostCentre(cc.id, {
              id: cc.id,
              projectId: numericProjectId,
              code: cc.code,
              name: cc.name,
              type: cc.type,
              status: cc.status,
              billable: false,
              owner: cc.owner,
              description: cc.description,
              costsBudget: cc.costsBudget,
              actualCosts: cc.actualCosts,
              committedCosts: cc.committedCosts,
              revenueBudget: cc.revenueBudget,
              actualRevenue: cc.actualRevenue,
              hoursBudget: cc.hoursBudget,
              actualHours: cc.actualHours,
              isActive: cc.isActive,
            })
          );
        await Promise.all(cascadePromises);
        setCostCentres((prev) => prev.map((cc) => ({ ...cc, billable: false })));
      }

      // Save project settings for Time & Material projects
      if (project.type === "time-and-materials") {
        // TODO: Call API to save settings when backend is ready
        // await saveProjectSettings(numericProjectId, projectSettings);
      }

      const updatedProject = { ...project, ...editFormData };
      setProject(updatedProject);
      setIsProjectInfoEditMode(false);
      setEditFormData({});
      setHasUnsavedChanges(false);
      toast.success("Project updated successfully");

      window.dispatchEvent(
        new CustomEvent("project-updated", {
          detail: { projectId, project: updatedProject },
        }),
      );
    } catch (error) {
      toast.error("Failed to update project");
      console.error("Error saving project:", error);
    }
  };

  // Inline status change — saves immediately, no edit mode required
  const handleInlineStatusChange = async (newStatusId: string) => {
    if (!project) return;
    const newId = Number(newStatusId) as ProjectStatusId;
    if (newId === project.statusId) return;

    const previousStatusId = project.statusId;
    const newStatusLabel = statusLabels[newId] || PROJECT_STATUS_LABELS[newId] || String(newId);

    // Optimistic update
    setProject(prev => prev ? { ...prev, statusId: newId } : prev);

    try {
      setIsStatusSaving(true);
      await updateProject(numericProjectId, {
        id: numericProjectId,
        name: project.name,
        code: project.code,
        description: project.description,
        statusId: newId,
        type: project.type,
        revenueRecognitionMethod: project.revenueRecognitionMethod,
        startDate: project.startDate,
        endDate: project.endDate,
        clientIds: project.clientIds,
        projectManagerId: project.projectManagerId,
        health: project.health,
      });

      toast.success(`Status updated to "${newStatusLabel}"`);
      window.dispatchEvent(
        new CustomEvent('project-updated', {
          detail: { projectId, project: { ...project, statusId: newId } },
        }),
      );
    } catch (error) {
      // Rollback on failure
      setProject(prev => prev ? { ...prev, statusId: previousStatusId } : prev);
      toast.error('Failed to update status');
      console.error('Error updating project status:', error);
    } finally {
      setIsStatusSaving(false);
    }
  };

  // Budget handlers
  const handleSaveBudget = async (
    updatedBudget: Partial<BudgetData>,
    revisionReason: string,
    saveAsBaseline: boolean,
  ) => {
    console.log("Saving budget:", { updatedBudget, revisionReason, saveAsBaseline });

    try {
      const result = await updateBudget(numericProjectId, updatedBudget);

      if (result.success) {
        console.log("[ProjectDetails] Budget saved successfully, refetching data...");
        toast.success("Budget saved successfully!");
        refetchBudget();
      } else {
        console.error("[ProjectDetails] Failed to save budget:", result.error);
        toast.error(result.error || "Failed to save budget");
      }
    } catch (error) {
      console.error("[ProjectDetails] Error saving budget:", error);
      toast.error("Failed to save budget");
    }

    // Close the appropriate modal
    setSalesBudgetEditModalOpen(false);
    setCostBudgetEditModalOpen(false);
    setResourceBudgetEditModalOpen(false);
  };

  const handleRestoreRevision = (revisionId: string) => {
    const rev = (enrichedBudgetData?.revisions ?? []).find(
      (r) => String(r.id) === revisionId || String(r.revisionId) === revisionId
    );
    if (rev) {
      setRevisionHistoryOpen(false);
      setMakeActiveSource('restore');
      setMakeActiveRevision(rev);
    }
  };

  const handleFieldChange = (field: keyof Project, value: any) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleProjectManagerChange = (employeeId: string, selectedEmployee: Employee | undefined) => {
    if (!selectedEmployee) return;
    // If PM is actually changing, require confirmation only if there is an existing PM
    if (selectedEmployee.id !== project?.projectManagerId) {
      if (!projectManager) {
        // No existing PM — assign directly without confirmation
        setProjectManager(selectedEmployee);
        setEditFormData((prev) => ({ ...prev, projectManagerId: selectedEmployee.id }));
        const updatedRecentPMs = [employeeId, ...recentProjectManagerIds.filter((id) => id !== employeeId)].slice(0, 10);
        setRecentProjectManagerIds(updatedRecentPMs);
        localStorage.setItem("recentProjectManagerIds", JSON.stringify(updatedRecentPMs));
        setHasUnsavedChanges(true);
      } else {
        // Existing PM — show reassignment confirmation
        setPendingPmChange({ id: employeeId, employee: selectedEmployee });
        setShowPmConfirmDialog(true);
      }
    }
  };

  const handleConfirmPmChange = () => {
    if (!pendingPmChange) return;
    const { id: employeeId, employee: selectedEmployee } = pendingPmChange;
    setProjectManager(selectedEmployee);
    setEditFormData((prev) => ({ ...prev, projectManagerId: selectedEmployee.id }));
    const updatedRecentPMs = [employeeId, ...recentProjectManagerIds.filter((id) => id !== employeeId)].slice(0, 10);
    setRecentProjectManagerIds(updatedRecentPMs);
    localStorage.setItem("recentProjectManagerIds", JSON.stringify(updatedRecentPMs));
    setHasUnsavedChanges(true);
    setShowPmConfirmDialog(false);
    setPendingPmChange(null);
  };

  const handleCancelPmChange = () => {
    setShowPmConfirmDialog(false);
    setPendingPmChange(null);
  };

  // ── Key People card — edit mode ──

  const handleEnterKeyPeopleEditMode = () => {
    if (!project) return;
    // Seed edit state from existing roleAssignments
    const seed: Partial<Record<ProjectRoleType, string>> = {};
    (project.roleAssignments ?? []).forEach((a) => {
      seed[a.role] = String(a.employeeId);
    });
    // C6: If no roleAssignments, seed PM from projectManagerId fallback
    if (!project.roleAssignments && project.projectManagerId) {
      seed['project-manager'] = String(project.projectManagerId);
    }
    setKeyPeopleEditState(seed);
    setHasKeyPeopleUnsavedChanges(false);
    setIsKeyPeopleEditMode(true);
  };

  const handleCancelKeyPeopleEdit = () => {
    setIsKeyPeopleEditMode(false);
    setKeyPeopleEditState({});
    setHasKeyPeopleUnsavedChanges(false);
  };

  const handleKeyPeopleRoleChange = (role: ProjectRoleType, value: string) => {
    setKeyPeopleEditState((prev) => ({ ...prev, [role]: value }));
    setHasKeyPeopleUnsavedChanges(true);
  };

  const handleSaveKeyPeople = async () => {
    if (!project) return;
    if (isSavingKeyPeople) return;
    // Build updated roleAssignments from edit state
    const updatedAssignments: ProjectRoleAssignment[] = [];
    PROJECT_ROLE_DISPLAY_ORDER.forEach((role) => {
      const empIdStr = keyPeopleEditState[role];
      if (!empIdStr) return; // cleared / not assigned
      const emp = employees.find((e) => String(e.id) === empIdStr);
      if (!emp) return;
      updatedAssignments.push({
        id: Date.now() + Math.random(), // temp id — replaced by backend on next load
        projectId: project.id,
        employeeId: emp.id,
        employeeName: emp.name,
        employeeJobTitle: emp.role ?? undefined,
        role,
      });
    });

    const dataMode = getDataMode();
    if (dataMode === 'live') {
      setIsSavingKeyPeople(true);
      try {
        const assignmentItems = updatedAssignments.map((a) => ({
          role: a.role,
          employeeId: a.employeeId,
          employeeName: a.employeeName,
          employeeJobTitle: a.employeeJobTitle ?? null,
        }));
        await saveProjectRoleAssignments(project.id, assignmentItems);
        setProject({ ...project, roleAssignments: updatedAssignments });
        setIsKeyPeopleEditMode(false);
        setKeyPeopleEditState({});
        setHasKeyPeopleUnsavedChanges(false);
        toast.success('Key people updated.');
      } catch (err) {
        console.error('Failed to save key people:', err);
        toast.error('Failed to save key people.');
      } finally {
        setIsSavingKeyPeople(false);
      }
    } else {
      setProject({ ...project, roleAssignments: updatedAssignments });
      setIsKeyPeopleEditMode(false);
      setKeyPeopleEditState({});
      setHasKeyPeopleUnsavedChanges(false);
      toast.success('Key people updated.');
    }
  };

  // ── Project Content cards — Objectives, Key Deliverables, Notes (Workstream E) ──

  const handleEnterObjectivesEdit = () => {
    setObjectivesEditValue(project?.objectives ?? "");
    setIsObjectivesEditMode(true);
  };
  const handleCancelObjectivesEdit = () => {
    setIsObjectivesEditMode(false);
    setObjectivesEditValue("");
  };
  const handleSaveObjectives = async () => {
    if (!project) return;
    if (isSavingObjectives) return;
    const dataMode = getDataMode();
    const value = objectivesEditValue.trim() || undefined;
    if (dataMode === "live") {
      setIsSavingObjectives(true);
      try {
        await saveProjectObjectives(numericProjectId, value ?? null);
        setProject({ ...project, objectives: value });
        setIsObjectivesEditMode(false);
        setObjectivesEditValue("");
        toast.success("Objectives saved.");
      } catch {
        toast.error("Failed to save objectives.");
      } finally {
        setIsSavingObjectives(false);
      }
    } else {
      setProject({ ...project, objectives: value });
      setIsObjectivesEditMode(false);
      setObjectivesEditValue("");
      toast.success("Objectives saved.");
    }
  };

  const handleEnterDeliverablesEdit = () => {
    setDeliverablesEditValue(project?.keyDeliverables ?? "");
    setIsDeliverablesEditMode(true);
  };
  const handleCancelDeliverablesEdit = () => {
    setIsDeliverablesEditMode(false);
    setDeliverablesEditValue("");
  };
  const handleSaveDeliverables = async () => {
    if (!project) return;
    if (isSavingDeliverables) return;
    const dataMode = getDataMode();
    const value = deliverablesEditValue.trim() || undefined;
    if (dataMode === "live") {
      setIsSavingDeliverables(true);
      try {
        await saveProjectKeyDeliverables(numericProjectId, value ?? null);
        setProject({ ...project, keyDeliverables: value });
        setIsDeliverablesEditMode(false);
        setDeliverablesEditValue("");
        toast.success("Key deliverables saved.");
      } catch {
        toast.error("Failed to save key deliverables.");
      } finally {
        setIsSavingDeliverables(false);
      }
    } else {
      setProject({ ...project, keyDeliverables: value });
      setIsDeliverablesEditMode(false);
      setDeliverablesEditValue("");
      toast.success("Key deliverables saved.");
    }
  };

  // ── Note log — add a new entry ──
  const handleAddNote = async () => {
    if (!project || !newNoteValue.trim()) return;
    setIsSubmittingNote(true);

    // Derive initials from authenticated user name
    const name = authenticatedUser?.name ?? "Unknown User";
    const initials = name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0].toUpperCase())
      .join("");

    const dataMode = getDataMode();
    if (dataMode === "live") {
      try {
        const created = await createProjectNote(numericProjectId, newNoteValue.trim(), name, initials);
        setNoteLog((prev) => [created, ...prev]);
        setNewNoteValue("");
        toast.success("Note added.");
      } catch {
        toast.error("Failed to add note.");
      } finally {
        setIsSubmittingNote(false);
      }
    } else {
      const newEntry: ProjectNote = {
        id: crypto.randomUUID(),
        projectId: numericProjectId,
        content: newNoteValue.trim(),
        createdAt: new Date().toISOString(),
        createdByName: name,
        createdByInitials: initials,
      };
      setNoteLog((prev) => [newEntry, ...prev]);
      setNewNoteValue("");
      setIsSubmittingNote(false);
      toast.success("Note added.");
    }
  };

  // ── Project Attributes card — edit mode ──

  const handleEnterAttributesEditMode = () => {
    if (!project) return;
    setAttributesEditState({ ...(project.projectCustomFields ?? {}) });
    setIsAttributesEditMode(true);
  };

  const handleCancelAttributesEdit = () => {
    setIsAttributesEditMode(false);
    setAttributesEditState({});
    setAttributesValidationErrors({});
  };

  const handleSaveAttributes = async () => {
    if (!project) return;

    // Validate required fields
    const errors: Record<string, string> = {};
    for (const def of customFieldDefs) {
      if (def.required && def.enabled) {
        const val = attributesEditState[def.id] ?? '';
        if (val.trim() === '') {
          errors[def.id] = `${def.label} is required`;
        }
      }
    }
    if (Object.keys(errors).length > 0) {
      setAttributesValidationErrors(errors);
      toast.error('Please fill in all required fields.');
      return;
    }
    setAttributesValidationErrors({});

    if (isSavingAttributes) return;
    setIsSavingAttributes(true);
    try {
      const dataMode = getDataMode();
      let savedFields = { ...attributesEditState };
      if (dataMode === 'live') {
        savedFields = await saveProjectCustomFieldValues(numericProjectId, attributesEditState);
      } else {
        savedFields = await saveProjectCustomFieldValues(lookupProjectId, attributesEditState);
      }
      const updatedProject = { ...project, projectCustomFields: savedFields };
      setProject(updatedProject);
      setIsAttributesEditMode(false);
      setAttributesEditState({});
      toast.success("Project attributes updated.");
      window.dispatchEvent(
        new CustomEvent("project-updated", { detail: { projectId, project: updatedProject } })
      );
    } catch {
      toast.error("Failed to update project attributes.");
    } finally {
      setIsSavingAttributes(false);
    }
  };

  // ── Client Information card — edit mode ──

  const handleEnterClientInfoEditMode = () => {
    if (!project) return;
    setClientInfoEditData({ accountManagerId: project.accountManagerId });
    setIsClientInfoEditMode(true);
    setHasClientInfoUnsavedChanges(false);
  };

  const handleCancelClientInfoEdit = () => {
    setIsClientInfoEditMode(false);
    setClientInfoEditData({});
    setHasClientInfoUnsavedChanges(false);
    setPendingAmChange(null);
    setShowAmConfirmDialog(false);
  };

  const handleSaveClientInfoChanges = async () => {
    try {
      if (!project) return;
      await updateProject(numericProjectId, {
        id: numericProjectId,
        name: project.name,
        code: project.code,
        description: project.description,
        statusId: project.statusId,
        type: project.type,
        revenueRecognitionMethod: project.revenueRecognitionMethod,
        startDate: project.startDate,
        endDate: project.endDate,
        clientIds: clientInfoEditData.clientIds ?? project.clientIds,
        projectManagerId: project.projectManagerId,
        health: project.health,
        accountManagerId: clientInfoEditData.accountManagerId,
      });
      const updatedProject = { ...project, accountManagerId: clientInfoEditData.accountManagerId, clientIds: clientInfoEditData.clientIds ?? project.clientIds };
      setProject(updatedProject);
      setIsClientInfoEditMode(false);
      setClientInfoEditData({});
      setHasClientInfoUnsavedChanges(false);
      toast.success("Client information updated");
      window.dispatchEvent(new CustomEvent("project-updated", { detail: { projectId, project: updatedProject } }));
    } catch (error) {
      toast.error("Failed to update client information");
      console.error("Error saving client information:", error);
    }
  };

  // ── Account Manager change handlers ──

  const handleAccountManagerChange = (employeeId: string, selectedEmployee: Employee | undefined) => {
    if (!selectedEmployee) return;
    const currentAmId = clientInfoEditData.accountManagerId ?? project?.accountManagerId;
    if (selectedEmployee.id !== currentAmId) {
      if (!accountManager) {
        // No existing AM — assign directly without confirmation
        setAccountManager(selectedEmployee);
        setClientInfoEditData((prev) => ({ ...prev, accountManagerId: selectedEmployee.id }));
        const updatedRecentAMs = [employeeId, ...recentAccountManagerIds.filter((id) => id !== employeeId)].slice(0, 10);
        setRecentAccountManagerIds(updatedRecentAMs);
        localStorage.setItem("recentAccountManagerIds", JSON.stringify(updatedRecentAMs));
        setHasClientInfoUnsavedChanges(true);
      } else {
        // Existing AM — show reassignment confirmation
        setPendingAmChange({ id: employeeId, employee: selectedEmployee });
        setShowAmConfirmDialog(true);
      }
    }
  };

  const handleConfirmAmChange = () => {
    if (!pendingAmChange) return;
    const { id: employeeId, employee: selectedEmployee } = pendingAmChange;
    setAccountManager(selectedEmployee);
    setClientInfoEditData((prev) => ({ ...prev, accountManagerId: selectedEmployee.id }));
    const updatedRecentAMs = [employeeId, ...recentAccountManagerIds.filter((id) => id !== employeeId)].slice(0, 10);
    setRecentAccountManagerIds(updatedRecentAMs);
    localStorage.setItem("recentAccountManagerIds", JSON.stringify(updatedRecentAMs));
    setHasClientInfoUnsavedChanges(true);
    setShowAmConfirmDialog(false);
    setPendingAmChange(null);
  };

  const handleCancelAmChange = () => {
    setShowAmConfirmDialog(false);
    setPendingAmChange(null);
  };

  // ── Client change helpers (isClientChangeLocked declared after fixedFeeInvoices useMemo below) ──

  /** Write audit entries to both the project audit tab and the system-wide report */
  function writeClientChangeAuditEntries(prevClient: Client, newClient: Client) {
    const description = `Client changed from ${prevClient.name} (${prevClient.code}) to ${newClient.name} (${newClient.code})`;
    const changedByName = authenticatedUser?.name ?? "Unknown";

    // Project-level audit event
    const projectEvent: AuditEvent = {
      id: `client-change-${Date.now()}`,
      category: "client",
      changedAt: new Date().toISOString(),
      changedBy: changedByName,
      description,
      metadata: {
        previousClientId: prevClient.id,
        previousClientName: prevClient.name,
        previousClientCode: prevClient.code,
        newClientId: newClient.id,
        newClientName: newClient.name,
        newClientCode: newClient.code,
        projectFinancialStateAtChange: {
          actualRevenue: project?.actualRevenue ?? 0,
          wipAmount: project?.wipAmount ?? 0,
          actualHours: project?.actualHours ?? 0,
        },
      },
    };
    setClientAuditEvents((prev) => [...prev, projectEvent]);

    // System-wide audit log entry
    appendAuditEntry({
      category: "Client",
      action: "Changed",
      entityName: `Project: ${project?.name ?? ""} (${project?.code ?? ""})`,
      oldValue: `${prevClient.name} (${prevClient.code})`,
      newValue: `${newClient.name} (${newClient.code})`,
      detail: `Client reassigned on project. Financial state at time of change — Revenue: £${project?.actualRevenue ?? 0}, WIP: £${project?.wipAmount ?? 0}, Hours: ${project?.actualHours ?? 0}h`,
      changedBy: changedByName,
    });

    // Persist to backend in live mode so the events survive page refresh
    if (getDataMode() === 'live' && projectId) {
      const metadataJson = JSON.stringify({
        previousClientId: prevClient.id,
        newClientId: newClient.id,
        projectFinancialStateAtChange: {
          actualRevenue: project?.actualRevenue ?? 0,
          wipAmount: project?.wipAmount ?? 0,
          actualHours: project?.actualHours ?? 0,
        },
      });

      // Project-level audit log
      authFetch(`${getApiBaseUrl()}/projects/${projectId}/audit-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventType: 'project.client.changed',
          category: 'client',
          description,
          changedBy: changedByName,
          previousValue: `${prevClient.name} (${prevClient.code})`,
          newValue: `${newClient.name} (${newClient.code})`,
          metadata: metadataJson,
        }),
      }).catch((err) => {
        console.error('[Audit] Failed to persist client change to project audit log:', err);
      });

      // System-wide audit log
      authFetch(`${getApiBaseUrl()}/audit-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: 'Client',
          action: 'Changed',
          entityName: `Project: ${project?.name ?? ""} (${project?.code ?? ""})`,
          changedBy: changedByName,
          oldValue: `${prevClient.name} (${prevClient.code})`,
          newValue: `${newClient.name} (${newClient.code})`,
          detail: `Client reassigned on project. Financial state at time of change — Revenue: £${project?.actualRevenue ?? 0}, WIP: £${project?.wipAmount ?? 0}, Hours: ${project?.actualHours ?? 0}h`,
        }),
      }).catch((err) => {
        console.error('[Audit] Failed to persist client change to system audit log:', err);
      });
    }
  }

  /** Apply the client change to state (shared by silent + confirmed paths) */
  function applyClientChange(clientId: string, selectedClient: Client) {
    const previousClient = client;

    setClient(selectedClient);
    setEditFormData((prev) => ({ ...prev, clientIds: clientId }));
    setClientInfoEditData((prev) => ({ ...prev, clientIds: clientId }));

    const updatedRecentClients = [clientId, ...recentClientIds.filter((id) => id !== clientId)].slice(0, 10);
    setRecentClientIds(updatedRecentClients);
    localStorage.setItem("recentClientIds", JSON.stringify(updatedRecentClients));
    setHasUnsavedChanges(true);
    setHasClientInfoUnsavedChanges(true);

    // Write audit entries for every client change
    if (previousClient && previousClient.id !== selectedClient.id) {
      writeClientChangeAuditEntries(previousClient, selectedClient);
    }
  }

  const handleClientChange = (clientId: string, selectedClient: Client | undefined) => {
    if (!selectedClient) return;

    // Hard block — field should already be disabled, but guard defensively
    if (isClientChangeLocked) return;

    // Soft warning — show confirmation dialog
    if (requiresClientChangeConfirmation) {
      setPreviousClientForDialog(client);
      setPendingClientChange({ clientId, selectedClient });
      setClientChangeDialogOpen(true);
      return;
    }

    // Silent allow — Draft with no financial activity
    applyClientChange(clientId, selectedClient);
  };

  const handleClientChangeConfirm = () => {
    if (pendingClientChange) {
      applyClientChange(pendingClientChange.clientId, pendingClientChange.selectedClient);
    }
    setClientChangeDialogOpen(false);
    setPendingClientChange(null);
    setPreviousClientForDialog(null);
  };

  const handleClientChangeCancel = () => {
    setClientChangeDialogOpen(false);
    setPendingClientChange(null);
    setPreviousClientForDialog(null);
  };

  // Project Settings handlers
  const handleSettingsChange = (field: string, value: any) => {
    setProjectSettings((prev) => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  // Drawer-specific handlers
  const handleDrawerSettingsChange = (field: string, value: any) => {
    setDrawerSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleDrawerOpen = (open: boolean) => {
    setBillingSettingsDrawerOpen(open);
    if (open) {
      // Reset drawer state when opening
      setDrawerSettings(projectSettings);
      setDrawerEditMode(false);
    }
  };

  const handleDrawerEdit = () => {
    setDrawerEditMode(true);
  };

  const handleDrawerCancel = () => {
    setDrawerSettings(projectSettings);
    setDrawerEditMode(false);
  };

  const handleDrawerSave = async () => {
    // Validate: fixed-price projects must have a default cost centre for finance posting
    if (project?.type === "fixed-price" && !drawerSettings.defaultCostCentreCode) {
      setDefaultCostCentreError(true);
      return;
    }

    setDrawerSaving(true);
    try {
      // Persist ALL billing config fields via PUT /api/projects/{id}/billing-config
      if (numericProjectId) {
        // Map drawer field names back to API field names
        const expenseRechargeRule =
          drawerSettings.expenseRecharge === 'not-rechargeable' ? 'none' as const
          : drawerSettings.expenseRecharge === 'markup' ? 'marked-up' as const
          : 'at-cost' as const;
        const purchaseRechargeRule =
          drawerSettings.purchaseRecharge === 'not-rechargeable' ? 'none' as const
          : drawerSettings.purchaseRecharge === 'markup' ? 'marked-up' as const
          : 'at-cost' as const;

        await updateProjectBillingConfig(numericProjectId, {
          invoiceTemplateId: drawerSettings.invoiceTemplateId ?? null,
          invoiceEmailTemplateId: drawerSettings.invoiceEmailTemplateId ?? null,
          creditNoteEmailTemplateId: drawerSettings.creditNoteEmailTemplateId ?? null,
          billingFrequency: drawerSettings.billingFrequency as "weekly" | "fortnightly" | "monthly" | "quarterly" | "on-completion" | "milestone",
          autoGenerate: drawerSettings.autoGenerateEnabled,
          monthlyPattern: drawerSettings.monthlyPattern as 'specific-day' | 'last-day' | 'first-working-day' | 'last-working-day',
          monthlyDay: drawerSettings.monthlyDay,
          weeklyDay: drawerSettings.weeklyDay as 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday',
          defaultCostCentreCode: drawerSettings.defaultCostCentreCode ?? null,
          expenseRechargeRule,
          expenseMarkupPercent: drawerSettings.expenseMarkupPercent ? Number(drawerSettings.expenseMarkupPercent) : undefined,
          rechargesPurchases: drawerSettings.purchaseRecharge !== 'not-rechargeable',
          purchaseRechargeRule,
          purchaseMarkupPercent: drawerSettings.purchaseMarkupPercent ? Number(drawerSettings.purchaseMarkupPercent) : undefined,
          nteCapAmount: drawerSettings.notToExceedCapEnabled && drawerSettings.notToExceedCapAmount
            ? Number(drawerSettings.notToExceedCapAmount) : null,
          billingTriggerType: drawerSettings.billingTriggerType || undefined,
          completionPercentMode: project?.completionPercentMode,
        });

        // Update local project state so UI reflects the billingTriggerType change immediately
        if (project && drawerSettings.billingTriggerType) {
          setProject((prev) =>
            prev ? { ...prev, billingTriggerType: drawerSettings.billingTriggerType as 'date' | 'percent-complete' } : prev
          );
        }
      }

      // Update the main project settings
      setProjectSettings(drawerSettings);
      setDrawerEditMode(false);
    } catch (error) {
      console.error("Error saving settings:", error);
    } finally {
      setDrawerSaving(false);
    }
  };

  // ── Completion % override handlers (Phase 13.2 / 13.3) ──────────────────

  // ── Fixed-fee invoices — in live mode use real billing data from the API;
  // in demo mode fall back to mock invoices derived from milestone data.
  const fixedFeeInvoices = useMemo(() => {
    // Live mode: map real BillingInvoice[] from API into the shape FixedFeeBillingTab expects
    if (dataMode === "live") {
      return billingInvoices.map((inv) => ({
        id: inv.id,
        invoiceNumber: inv.invoiceNumber || null,
        date: inv.invoiceDate ? inv.invoiceDate.split("T")[0] : null,
        description: inv.description || "",
        amount: inv.grossTotal,
        subtotal: inv.subtotal,
        taxAmount: inv.taxAmount,
        due: inv.dueDate ? inv.dueDate.split("T")[0] : null,
        status: deriveInvoiceDisplayStatus(inv) as "Draft" | "Sent" | "Paid" | "Overdue" | "Credited" | "PartiallyCredited" | "CreditNote",
        milestoneId: inv.milestoneId ?? undefined,
      }));
    }

    // Demo mode: generate a realistic spread of invoices so the Invoice Summary card
    // always has Overdue and Draft rows to click through for testing (Task 26).
    const budget = project?.budgetRevenue ?? 0;
    const today = new Date();

    // Helper to format a date as YYYY-MM-DD
    const fmt = (d: Date) => d.toISOString().split('T')[0];

    // Helper to add days to a date
    const addDays = (d: Date, n: number) => {
      const r = new Date(d);
      r.setDate(r.getDate() + n);
      return r;
    };

    // Spread budget across 4 invoices: 30% / 25% / 25% / 20%
    const splits = [0.30, 0.25, 0.25, 0.20];
    const amounts = splits.map((s) => Math.round(budget * s));
    // Fix rounding so totals match exactly
    const diff = budget - amounts.reduce((a, b) => a + b, 0);
    amounts[0] += diff;

    // Invoice dates spread over the last ~9 months
    const invDates = [
      addDays(today, -270),
      addDays(today, -180),
      addDays(today, -75),
      addDays(today, -20),
    ];
    // Due dates = invoice date + 30 days
    const dueDates = invDates.map((d) => addDays(d, 30));

    // Statuses: Paid → Overdue → Sent → Draft
    // This ensures the card always has at least 1 Overdue and 1 Draft to click.
    const statuses: Array<"Paid" | "Overdue" | "Sent" | "Draft"> = ["Paid", "Overdue", "Sent", "Draft"];
    const invoiceNumbers = ["INV-FF-001", "INV-FF-002", null, null];

    return amounts.map((amount, i) => {
      const { net, vat } = splitGrossIntoNetAndVat(amount);
      return {
        id: 100 + i + 1,
        invoiceNumber: invoiceNumbers[i],
        date: fmt(invDates[i]),
        description: [
          'Mobilisation Payment',
          'Progress Claim — 50% Complete',
          'Progress Claim — Practical Completion',
          'Final Retention Payment',
        ][i],
        amount,
        subtotal: net,
        taxAmount: vat,
        due: fmt(dueDates[i]),
        status: statuses[i],
        milestoneId: undefined,
      };
    }).filter((inv) => inv.amount > 0);
  }, [dataMode, billingInvoices, project?.budgetRevenue]);

  /** Hard block — client field locked when project has financial transactions
   *  AND a client is already assigned. When no client is set yet the user must
   *  still be able to assign one regardless of financial state.
   *  Draft invoices are excluded — they carry no financial impact until Sent/Paid. */
  const isClientChangeLocked = Boolean(
    project && client && (
      (project.actualRevenue ?? 0) > 0 ||
      (project.wipAmount ?? 0) > 0 ||
      (project.unbilledAmount ?? 0) > 0 ||
      fixedFeeInvoices.some((inv) => inv.status !== 'Draft' && (inv.amount ?? 0) > 0)
    )
  );

  /** Soft warning — confirmation needed when project has approved hours */
  const requiresClientChangeConfirmation = Boolean(
    project && !isClientChangeLocked && (project.actualHours ?? 0) > 0
  );

  // ── Project Information field-level lock and warning computed values ──────

  /** Rev Rec — hard lock once any revenue has been recognised */
  const isRevRecLocked = Boolean(project && (project.actualRevenue ?? 0) > 0);

  /** Project Type — derive WIP invoice tier for three-tier lock rule */
  const wipInvoiceTier = useMemo<'none' | 'all-paid' | 'draft-exist' | 'sent-unpaid'>(() => {
    if (!fixedFeeInvoices.length) return 'none';
    const hasDraft = fixedFeeInvoices.some((i) => i.status === 'Draft');
    const hasSentUnpaid = fixedFeeInvoices.some((i) => i.status === 'Sent' || i.status === 'Overdue');
    if (hasSentUnpaid) return 'sent-unpaid';
    if (hasDraft) return 'draft-exist';
    return 'all-paid';
  }, [fixedFeeInvoices]);

  const isTypeLocked = wipInvoiceTier === 'sent-unpaid';

  /** Project Name — soft warning if sent/paid invoices exist */
  const projectNameHasIssuedInvoices = useMemo(
    () => fixedFeeInvoices.some((i) => i.status === 'Sent' || i.status === 'Paid' || i.status === 'Overdue'),
    [fixedFeeInvoices]
  );

  /** Date validation — computed against editFormData while in edit mode */
  const editStartDate = editFormData.startDate ?? project?.startDate ?? '';
  const editEndDate = editFormData.endDate ?? project?.endDate ?? '';
  const endDateBeforeStart = Boolean(
    editStartDate && editEndDate && new Date(editEndDate) < new Date(editStartDate)
  );

  /** Start Date — warn if moving start later than earliest time entry */
  const earliestTimeEntryDate = useMemo(() => {
    if (!timeEntries.length) return null;
    const sorted = [...timeEntries].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    return sorted[0].date;
  }, [timeEntries]);

  const startDateMovedPastTimesheets = Boolean(
    editFormData.startDate &&
    earliestTimeEntryDate &&
    new Date(editFormData.startDate) > new Date(earliestTimeEntryDate)
  );

  /** End Date — warn if shortening past latest resource allocation */
  const latestAllocationEndDate = useMemo(() => {
    if (!resourceAllocations.length) return null;
    const withEnd = resourceAllocations.filter((r) => r.endDate);
    if (!withEnd.length) return null;
    const sorted = [...withEnd].sort((a, b) => new Date(b.endDate!).getTime() - new Date(a.endDate!).getTime());
    return sorted[0].endDate!;
  }, [resourceAllocations]);

  const endDateShortenedPastAllocations = Boolean(
    editFormData.endDate &&
    latestAllocationEndDate &&
    new Date(editFormData.endDate) < new Date(latestAllocationEndDate)
  );

  /** Overall save block — true when any hard block condition is active.
   *  isTypeLocked is NOT included here because the type field is individually
   *  locked in the UI (rendered as a static div), so other fields (e.g. billable)
   *  can still be saved while the type is locked. */
  const isSaveBlocked = endDateBeforeStart;

  /** Authoritative gross "Invoiced" total for the project — every raised invoice
   *  except Draft and Void, net of credit notes. In live mode this is the
   *  server-derived TotalInvoiced from `usp_Invoice_GetByProjectIdPaged`; in demo
   *  mode the same definition applied to the demo invoice dataset. */
  const invoicedTotal = useMemo(
    () => (dataMode === "live" ? invoiceTotalInvoiced : computeInvoicedTotal(DEMO_INVOICES)),
    [dataMode, invoiceTotalInvoiced]
  );

  /** {@link invoicedTotal} excluding VAT — the basis to use when comparing against
   *  budget figures (which are net), e.g. the billing summary bar and the Overview
   *  Sales Variance card. */
  const invoicedNetTotal = useMemo(
    () => (dataMode === "live" ? invoiceTotalInvoicedNet : computeInvoicedNetTotal(DEMO_INVOICES)),
    [dataMode, invoiceTotalInvoicedNet]
  );

  /** Sum of Sent + Paid fixed-fee invoices — used by the Sales Budget Revenue Health panel. */
  const fixedFeeInvoicedToDate = useMemo(
    () => fixedFeeInvoices
      .filter((i) => (i.status as string) === 'Sent' || (i.status as string) === 'Paid')
      .reduce((s, i) => s + (i.subtotal ?? i.amount), 0),
    [fixedFeeInvoices]
  );

  /** Fixed-fee invoices shaped as AuditInvoice[] for the Audit tab (Phase 17).
   *  The fixedFeeInvoices items don't carry createdDate/sentDate/paidDate in the
   *  current mock — we derive them from the invoice date field where possible.
   *  When the backend is live (17-BE.3) this memo is replaced by API data.
   */
  const auditInvoices = useMemo<AuditInvoice[]>(
    () =>
      fixedFeeInvoices.map((inv) => ({
        id: inv.id,
        invoiceNumber: inv.invoiceNumber,
        date: inv.date,
        dueDate: inv.due,
        description: inv.description,
        amount: inv.amount,
        status: inv.status as string,
        milestoneId: inv.milestoneId,
        // Derive event timestamps from status and the invoice date field
        createdDate: inv.date ?? undefined,
        sentDate: (inv.status === 'Paid' || inv.status === 'Sent') && inv.date ? inv.date : undefined,
        paidDate: inv.status === 'Paid' && inv.due ? inv.due : undefined,
      })),
    [fixedFeeInvoices]
  );

  /** Auto-calculated % from approved hours ÷ budget hours (mirrors based-on-time logic).
   *  Uses enriched budget data when available so the figure is consistent with the
   *  Hour Utilisation card which also prefers the ResourceBudget totals. */
  const _completionHours = {
    budgetHours: enrichedBudgetData?.summary?.hours?.budget,
    // Enriched actuals of zero fall back to the project total, matching the
    // Hour Utilisation card.
    actualHours: enrichedBudgetData?.summary?.hours?.actual || project?.actualHours,
  };
  const autoCalculatedCompletionPercent = project
    ? computeAutoCalculatedCompletionPercent(project, _completionHours)
    : 0;

  /** The effective completion %, respecting the PM override and calculation mode.
   *  When mode is 'based-on-time' (the default) the value is derived from hours
   *  rather than reading the stale Project.CompletionPercent column. */
  const effectiveCompletionPercent = project
    ? computeEffectiveCompletionPercent(project, _completionHours)
    : 0;

  const handleCompletionPercentEditStart = () => {
    setCompletionPercentInputValue(String(effectiveCompletionPercent));
    setCompletionPercentEditing(true);
  };

  const handleCompletionPercentEditConfirm = async () => {
    if (!project) return;
    const parsed = parseInt(completionPercentInputValue, 10);
    if (isNaN(parsed) || parsed < 0 || parsed > 100) {
      setCompletionPercentEditing(false);
      return;
    }
    const now = new Date().toISOString();
    const overriddenBy = authenticatedUser?.name ?? 'Unknown User';
    setProject((prev) =>
      prev
        ? {
            ...prev,
            completionPercentOverride: parsed,
            completionPercentOverriddenBy: overriddenBy,
            completionPercentOverriddenAt: now,
            completionPercentMode: 'manual',
          }
        : prev
    );
    setCompletionPercentEditing(false);

    if (numericProjectId) {
      try {
        await updateProject(numericProjectId, {
          id: numericProjectId,
          completionPercentOverride: parsed,
          completionPercentOverriddenBy: overriddenBy,
          completionPercentOverriddenAt: now,
          completionPercentMode: 'manual',
        });
      } catch (error) {
        console.error('Failed to persist completion % override:', error);
      }
    }
  };

  const handleCompletionPercentEditCancel = () => {
    setCompletionPercentEditing(false);
  };

  /** Switch calculation mode in the Billing Settings drawer. */
  const handleCompletionModeChange = async (mode: 'manual' | 'based-on-time') => {
    setProject((prev) => {
      if (!prev) return prev;
      if (mode === 'based-on-time') {
        // Recalculate from hours; clear any override
        return {
          ...prev,
          completionPercentMode: 'based-on-time',
          completionPercentOverride: undefined,
          completionPercentOverriddenBy: undefined,
          completionPercentOverriddenAt: undefined,
        };
      }
      // switching to manual — freeze current value
      return {
        ...prev,
        completionPercentMode: 'manual',
      };
    });

    if (numericProjectId) {
      try {
        if (mode === 'based-on-time') {
          await updateProject(numericProjectId, {
            id: numericProjectId,
            completionPercentMode: 'based-on-time',
            completionPercentOverride: null,
            completionPercentOverriddenBy: null,
            completionPercentOverriddenAt: null,
          });
        } else {
          await updateProject(numericProjectId, {
            id: numericProjectId,
            completionPercentMode: 'manual',
          });
        }
      } catch (error) {
        console.error('Failed to persist completion % mode change:', error);
      }
    }
  };

  // Calculate next invoice generation date based on settings (for drawer)
  const getNextInvoiceDateForDrawer = () => {
    if (!drawerSettings.autoGenerateEnabled) return null;
    return getNextAutoInvoiceDate(drawerSettings, lastAutoGeneratedAt);
  };

  // Calculate next invoice generation date based on settings
  const getNextInvoiceDate = () => {
    if (!projectSettings.autoGenerateEnabled) return null;
    return getNextAutoInvoiceDate(projectSettings, lastAutoGeneratedAt);
  };

  const validateSettings = () => {
    // Validate inputs
    if (projectSettings.expenseRecharge === "markup" && !projectSettings.expenseMarkupPercent) {
      toast.error("Please enter expense markup percentage");
      return false;
    }
    if (projectSettings.purchaseRecharge === "markup" && !projectSettings.purchaseMarkupPercent) {
      toast.error("Please enter purchase markup percentage");
      return false;
    }
    if (projectSettings.notToExceedCapEnabled && !projectSettings.notToExceedCapAmount) {
      toast.error("Please enter invoice cap amount");
      return false;
    }
    return true;
  };

  // Warn user about unsaved changes when navigating away (E7: covers all editable cards)
  useEffect(() => {
    const anyUnsaved =
      hasUnsavedChanges ||
      isObjectivesEditMode ||
      isDeliverablesEditMode ||
      isKeyPeopleEditMode ||
      isClientInfoEditMode;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (anyUnsaved) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges, isObjectivesEditMode, isDeliverablesEditMode, isKeyPeopleEditMode, isClientInfoEditMode]);

  // Legacy event listener — kept for backwards compat but no longer triggered from UI
  useEffect(() => {
    const handleEditMode = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.projectId === projectId) {
        handleEnterEditMode();
      }
    };

    window.addEventListener("project-edit-mode", handleEditMode);
    return () => window.removeEventListener("project-edit-mode", handleEditMode);
  }, [projectId, handleEnterEditMode]);

  // Probe the scheduler connection when the resourcing tab is activated (deferred to avoid
  // unnecessary requests on initial project load when the overview tab is shown). Only a
  // single booking is requested — the Bookings table fetches its own pages on demand.
  const schedulingDataLoadedRef = useRef(false);
  useEffect(() => {
    if (activeTab !== 'resourcing' && !schedulingDataLoadedRef.current) return;
    if (!projectId || !schedulerConnected) return;
    schedulingDataLoadedRef.current = true;
    let cancelled = false;

    async function loadSchedulingData() {
      setSchedulingLoading(true);
      setSchedulingError(null);
      try {
        const response = await fetchSchedulingBookings(projectId, { page: 1, pageSize: 1 });
        if (cancelled) return;
        setSchedulingBookings(response.bookings);
        setSchedulingConnectionStatus(response.connectionStatus);
        setSchedulingSchedulerName(response.schedulerName);
        setSchedulerUrl(response.schedulerUrl ?? null);
      } catch (err) {
        if (cancelled) return;
        console.error('[ProjectDetails] Failed to fetch scheduling bookings:', err);
        setSchedulingError('Unable to load scheduling data.');
        setSchedulingConnectionStatus('error');
        setSchedulingBookings([]);
      } finally {
        if (!cancelled) setSchedulingLoading(false);
      }
    }

    loadSchedulingData();
    return () => { cancelled = true; };
  }, [projectId, schedulerConnected, resourceRequestsRefreshKey, activeTab]);

  // Fetch resource requests when the resourcing tab is activated
  const resourceRequestsLoadedRef = useRef(false);
  useEffect(() => {
    if (activeTab !== 'resourcing' && !resourceRequestsLoadedRef.current) return;
    if (!projectId) return;
    resourceRequestsLoadedRef.current = true;
    let cancelled = false;

    async function loadResourceRequests() {
      setResourceRequestsLoading(true);
      try {
        const data = await fetchResourceRequests(projectId);
        if (cancelled) return;
        // Map API response to the ResourceRequest shape the component expects
        const mapped: ResourceRequest[] = data.map((r: ResourceRequestResponse) => ({
          requestId: r.requestId,
          roleName: r.roleName,
          hoursRequired: r.hoursRequired,
          allocatedHours: r.allocatedHours,
          startDate: r.desiredStartDate,
          endDate: r.desiredEndDate,
          priority: r.priority as ResourceRequest['priority'],
          status: r.status as ResourceRequest['status'],
          notes: r.notes,
          raisedAt: r.raisedAt,
          assignedTo: r.assignedTo,
          assignedResources: r.assignedResources,
        }));
        setLiveResourceRequests(mapped);
      } catch (err) {
        if (cancelled) return;
        console.error('[ProjectDetails] Failed to fetch resource requests:', err);
        setLiveResourceRequests([]);
      } finally {
        if (!cancelled) setResourceRequestsLoading(false);
      }
    }

    loadResourceRequests();
    return () => { cancelled = true; };
  }, [projectId, resourceRequestsRefreshKey, activeTab]);

  useEffect(() => {
    let ignore = false;
    async function loadData() {
      setLoading(true);
      setTimeEntriesLoading(true);
      try {
        const [currentProject, clientsData, employeesData, assignableEmployeesData, resourceAllocationsData] = await Promise.all([
          fetchProjectById(lookupProjectId),
          fetchClients("projects"),
          fetchEmployees("projects"),
          fetchEmployees("projects", { companyAccessOnly: true }),
          fetchResourceAllocations("projects"),
        ]);

        if (ignore) return;

        if (currentProject) {
          setProject(currentProject);
          const dataMode = getDataMode();

          setClients(clientsData); // Store all clients for the dropdown
          // Get the first client ID from the comma-separated list
          const firstClientId = currentProject.clientIds?.split(",")[0]?.trim();
          const matchedClient = clientsData.find((c) => c.id === parseInt(firstClientId || "0", 10));
          if (matchedClient) {
            setClient(matchedClient);
          } else if (firstClientId) {
            // Client exists in project data but not in the clients list (e.g. data sync
            // or RLS filtering). Construct a minimal Client from the project's own fields
            // so the detail page still shows the client name instead of "Unknown".
            const firstClientName = currentProject.clientNames?.split(",")[0]?.trim();
            setClient({
              id: parseInt(firstClientId, 10),
              name: firstClientName || `Client #${firstClientId}`,
              contactName: '',
              contactEmail: '',
            });
          } else {
            setClient(null);
          }
          setProjectManager(employeesData.find((e) => e.id === currentProject.projectManagerId) || null);
          setAccountManager(currentProject.accountManagerId ? (employeesData.find((e) => e.id === currentProject.accountManagerId) || null) : null);

          // Get resource allocations for this project
          const projectAllocations = resourceAllocationsData.filter((r) => r.projectId === numericProjectId);
          setResourceAllocations(projectAllocations);
          setAllResourceAllocations(resourceAllocationsData);
          setEmployees(employeesData);
          setAssignableEmployees(assignableEmployeesData);

          // Core data is ready — unblock the page so the Overview tab can render
          setLoading(false);

          // --- Background fetches: update individual state slices as they resolve ---

          // Seed note log — use API in live mode, fall back to project data in demo/tour
          if (dataMode === "live") {
            fetchProjectNotes(numericProjectId).then((notes) => {
              if (!ignore) setNoteLog(notes);
            }).catch(() => {
              if (!ignore) setNoteLog([]);
            });
          } else {
            setNoteLog(currentProject.noteLog ?? []);
          }

          // Load role assignments from API (live mode)
          if (dataMode === 'live') {
            fetchProjectRoleAssignments(numericProjectId).then((roleAssignmentsData) => {
              if (!ignore && roleAssignmentsData.length > 0) {
                const mapped: ProjectRoleAssignment[] = roleAssignmentsData.map((a) => ({
                  id: 0, // placeholder — not used for display
                  projectId: a.projectId,
                  employeeId: a.employeeId,
                  employeeName: a.employeeName,
                  employeeJobTitle: a.employeeJobTitle ?? undefined,
                  role: a.role as ProjectRoleType,
                }));
                currentProject.roleAssignments = mapped;
                setProject({ ...currentProject });
              }
            }).catch((err) => {
              console.error('Failed to load role assignments:', err);
            });
          }

          // Load custom field definitions and per-project values from API
          if (dataMode === 'live') {
            Promise.all([
              fetchCustomFieldDefinitions(),
              fetchProjectCustomFieldValues(numericProjectId),
            ]).then(([defsResponse, valuesResponse]) => {
              if (!ignore) {
                // Map backend definitions to frontend type
                const mapped = defsResponse
                  .filter((d: CustomFieldDefinitionResponse) => d.enabled)
                  .map((d: CustomFieldDefinitionResponse) => {
                    let parsedOptions: string[] | undefined;
                    if (d.options) {
                      try { const p = JSON.parse(d.options); if (Array.isArray(p)) parsedOptions = p; } catch { /* ignore */ }
                    }
                    return {
                      id: d.id,
                      label: d.label,
                      fieldType: d.fieldType as import('@/types').CustomFieldType,
                      required: d.required,
                      enabled: d.enabled,
                      displayOrder: d.displayOrder,
                      options: parsedOptions,
                      placeholder: d.placeholder ?? undefined,
                    };
                  });
                setCustomFieldDefs(mapped);
                // Merge API values into the project object
                if (Object.keys(valuesResponse).length > 0) {
                  currentProject.projectCustomFields = valuesResponse;
                  setProject({ ...currentProject });
                }
              }
            }).catch((err) => {
              console.error('Failed to load custom field data:', err);
            });
          } else {
            // Demo/tour mode — load from localStorage
            import('@/lib/project-custom-fields-storage').then(({ getProjectCustomFieldDefinitions }) => {
              const defs = getProjectCustomFieldDefinitions().filter((d) => d.enabled);
              if (!ignore) setCustomFieldDefs(defs);
            }).catch(() => { /* ignore */ });
          }

          if (dataMode === "live") {
            // Live mode — fetch time entries, milestones, change requests,
            // team membership, and timesheet fallback in the background
            (async () => {
              try {
                const [apiTimeEntries, apiMilestones, apiChangeRequests] = await Promise.all([
                  fetchTimeEntriesByProjectId(numericProjectId),
                  fetchMilestonesByProjectId(numericProjectId),
                  fetchChangeRequestsByProjectId(numericProjectId),
                ]);
                if (ignore) return;
                setTimeEntries(apiTimeEntries);
                setMilestones(apiMilestones);
                setChangeRequests(apiChangeRequests);

                // Fetch expenses in background — don't block page load
                setExpensesLoading(true);
                fetchExpensesByProjectId(
                  numericProjectId,
                  currentProject.code,
                  currentCompany,
                ).then((apiExpenses) => {
                  if (!ignore) setExpenses(apiExpenses);
                }).catch((err) => {
                  console.error('[Expense API] Background fetch failed:', err);
                  if (!ignore) setExpenses([]);
                }).finally(() => {
                  if (!ignore) setExpensesLoading(false);
                });

                const timeByEmployee = buildApprovedHoursByEmployee(apiTimeEntries);

                // Fetch team exclusions for this project
                let excludedEmployeeIds: Set<number> = new Set();
                try {
                  const exclusions = await fetchTeamExclusionsByProjectId(numericProjectId);
                  excludedEmployeeIds = new Set(exclusions.map((e) => e.employeeId));
                } catch (err) {
                  console.warn('[Team] Failed to fetch team exclusions:', err);
                }

                if (ignore) return;

                setExcludedTeamEmployeeIds(excludedEmployeeIds);
                setTeamMembers(buildTeamMembers(
                  employeesData,
                  projectAllocations,
                  timeByEmployee,
                  excludedEmployeeIds,
                ));

                // Billing data is lazy-loaded when the Billing tab is activated
                // (see useEffect for activeTab === "billing" below)
              } catch (error) {
                console.error("Failed to fetch project details from API:", error);
                if (!ignore) {
                  setTimeEntries([]);
                  setMilestones([]);
                  setChangeRequests([]);
                  setTeamMembers([]);
                  setBillingWipItems([]);
                  setBillingInvoices([]);
                }
              } finally {
                if (!ignore) setTimeEntriesLoading(false);
              }
            })();
          } else {
            // Demo mode - generate mock data
            // Mock time entries - include both allocated and unallocated employees
            const WORK_TYPES = ['Standard Time', 'Overtime', 'Night Shift', 'Weekend', 'Bank Holiday', 'On Call', 'Standby'];
            const ROLES = [
              { roleId: 'role-1', roleName: 'Project Manager' },
              { roleId: 'role-2', roleName: 'Senior Consultant' },
              { roleId: 'role-3', roleName: 'Consultant' },
              { roleId: 'role-4', roleName: 'Junior Consultant' },
              { roleId: 'role-5', roleName: 'Technical Architect' },
              { roleId: 'role-6', roleName: 'Business Analyst' },
              { roleId: 'role-7', roleName: 'Developer' },
              { roleId: 'role-8', roleName: 'QA Tester' },
            ];
            const COST_CENTRES = ['CC-001 Operations', 'CC-002 Technology', 'CC-003 Consulting', 'CC-004 Support'];

            const mockTimeEntries: TimeEntry[] = projectAllocations.slice(0, 10).map((alloc, i) => {
              const isApproved = Math.random() > 0.3;
              const entryDate = new Date(Date.now() - i * 86400000);
              const role = ROLES[i % ROLES.length];
              const employee = employeesData.find((e) => e.id === alloc.employeeId);

              return {
                id: mockSeedProjectId * 10000 + i,
                externalTimesheetId: `TS-2024-${1000 + i}`,
                projectId: mockSeedProjectId,
                employeeId: alloc.employeeId,
                employeeName: employee?.name,
                roleId: role.roleId,
                workTypeId: WORK_TYPES[i % WORK_TYPES.length],
                costCentreId: COST_CENTRES[i % COST_CENTRES.length],
                date: entryDate.toISOString().split("T")[0],
                hours: 6 + Math.random() * 3,
                description: "Development work",
                billable: Math.random() > 0.2,
                approved: isApproved,
                approvalHistory: [
                  {
                    date: new Date(entryDate.getTime() + 3600000).toISOString(),
                    action: "Submitted for Approval",
                    byUser: employeesData.find((e) => e.id === alloc.employeeId)?.name || "Employee",
                    status: "Pending Approval",
                  },
                  ...(isApproved
                    ? [
                        {
                          date: new Date(entryDate.getTime() + 7200000).toISOString(),
                          action: "Approved",
                          byUser: "Project Manager",
                          toUser: "Finance Team",
                          stage: "Manager Approval",
                          status: "Approved",
                        },
                      ]
                    : []),
                ],
                costRate: 50 + Math.random() * 30,
                billRate: 100 + Math.random() * 60,
              };
            });

            // Add time entries from employees NOT allocated to the project (to demonstrate unallocated tracking)
            const mockAllocatedEmployeeIds = new Set(projectAllocations.map((a) => a.employeeId));
            const unallocatedEmployees = employeesData.filter((e) => !mockAllocatedEmployeeIds.has(e.id)).slice(0, 2);

            unallocatedEmployees.forEach((employee, i) => {
              // Add 3-5 time entries for each unallocated employee
              const entryCount = 3 + Math.floor(Math.random() * 3);
              for (let j = 0; j < entryCount; j++) {
                const isApproved = Math.random() > 0.4;
                const entryDate = new Date(Date.now() - (i * 7 + j) * 86400000);

                const uRole = ROLES[(i * 3 + j) % ROLES.length];
                mockTimeEntries.push({
                  id: mockSeedProjectId * 10000 + 1000 + i * 10 + j,
                  externalTimesheetId: `TS-2024-${2000 + i * 10 + j}`,
                  projectId: mockSeedProjectId,
                  employeeId: employee.id,
                  employeeName: employee.name,
                  roleId: uRole.roleId,
                  workTypeId: WORK_TYPES[(i * 3 + j) % WORK_TYPES.length],
                  costCentreId: COST_CENTRES[(i + j) % COST_CENTRES.length],
                  date: entryDate.toISOString().split("T")[0],
                  hours: 4 + Math.random() * 4,
                  description: "Ad-hoc support work",
                  billable: Math.random() > 0.3,
                  approved: isApproved,
                  approvalHistory: [
                    {
                      date: new Date(entryDate.getTime() + 3600000).toISOString(),
                      action: "Submitted for Approval",
                      byUser: employee.name,
                      status: "Pending Approval",
                    },
                    ...(isApproved
                      ? [
                          {
                            date: new Date(entryDate.getTime() + 7200000).toISOString(),
                            action: "Approved",
                            byUser: "Project Manager",
                            toUser: "Finance Team",
                            stage: "Manager Approval",
                            status: "Approved",
                          },
                        ]
                      : []),
                  ],
                  costRate: 50 + Math.random() * 30,
                  billRate: 100 + Math.random() * 60,
                });
              }
            });

            setTimeEntries(mockTimeEntries);
            setTimeEntriesLoading(false);

            // Mock expenses
            const mockExpenses: Expense[] = [
              {
                id: mockSeedProjectId * 1000 + 1,
                projectId: mockSeedProjectId,
                employeeId: projectAllocations[0]?.employeeId || "e1",
                date: new Date(Date.now() - 5 * 86400000).toISOString().split("T")[0],
                category: "Travel",
                amount: 450,
                description: "Client site visit",
                billable: true,
                approved: true,
                approvalHistory: [
                  {
                    date: new Date(Date.now() - 5 * 86400000 + 3600000).toISOString(),
                    action: "Submitted for Approval",
                    byUser:
                      employeesData.find((e) => e.id === (projectAllocations[0]?.employeeId || "e1"))?.name ||
                      "Employee",
                    status: "Pending Approval",
                  },
                  {
                    date: new Date(Date.now() - 5 * 86400000 + 7200000).toISOString(),
                    action: "Approved",
                    byUser: "Project Manager",
                    toUser: "Finance Team",
                    stage: "Manager Approval",
                    status: "Approved",
                  },
                ],
              },
              {
                id: mockSeedProjectId * 1000 + 2,
                projectId: mockSeedProjectId,
                employeeId: projectAllocations[1]?.employeeId || "e2",
                date: new Date(Date.now() - 10 * 86400000).toISOString().split("T")[0],
                category: "Software",
                amount: 299,
                description: "Development tools licence",
                billable: true,
                approved: false,
                approvalHistory: [
                  {
                    date: new Date(Date.now() - 10 * 86400000 + 3600000).toISOString(),
                    action: "Submitted for Approval",
                    byUser:
                      employeesData.find((e) => e.id === (projectAllocations[1]?.employeeId || "e2"))?.name ||
                      "Employee",
                    status: "Pending Approval",
                  },
                ],
              },
            ];
            setExpenses(mockExpenses);

            // Mock milestones
            const mockMilestones: Milestone[] = [
              {
                id: mockSeedProjectId * 100 + 1,
                projectId: mockSeedProjectId,
                name: "Project Kickoff",
                dueDate: currentProject.startDate,
                completedDate: currentProject.startDate,
                status: "completed",
                paymentAmount: currentProject.budgetRevenue * 0.2,
                paymentReceived: true,
              },
              {
                id: mockSeedProjectId * 100 + 2,
                projectId: mockSeedProjectId,
                name: "Phase 1 Delivery",
                dueDate: new Date(new Date(currentProject.startDate).getTime() + 60 * 86400000)
                  .toISOString()
                  .split("T")[0],
                completedDate: new Date(new Date(currentProject.startDate).getTime() + 58 * 86400000)
                  .toISOString()
                  .split("T")[0],
                status: "completed",
                paymentAmount: currentProject.budgetRevenue * 0.3,
                paymentReceived: true,
              },
              {
                id: mockSeedProjectId * 100 + 3,
                projectId: mockSeedProjectId,
                name: "Phase 2 Delivery",
                dueDate: new Date(new Date(currentProject.startDate).getTime() + 120 * 86400000)
                  .toISOString()
                  .split("T")[0],
                status: currentProject.completionPercent > 70 ? "completed" : "pending",
                completedDate:
                  currentProject.completionPercent > 70
                    ? new Date(new Date(currentProject.startDate).getTime() + 118 * 86400000)
                        .toISOString()
                        .split("T")[0]
                    : undefined,
                paymentAmount: currentProject.budgetRevenue * 0.3,
                paymentReceived: false,
              },
              {
                id: mockSeedProjectId * 100 + 4,
                projectId: mockSeedProjectId,
                name: "Final Delivery",
                dueDate: currentProject.endDate,
                status: currentProject.status === "completed" ? "completed" : "pending",
                completedDate: currentProject.status === "completed" ? currentProject.endDate : undefined,
                paymentAmount: currentProject.budgetRevenue * 0.2,
                paymentReceived: false,
              },
            ];
            setMilestones(mockMilestones);

            // Mock change requests
            const mockChangeRequests: ChangeRequest[] = [
              {
                id: mockSeedProjectId * 100 + 1,
                projectId: mockSeedProjectId,
                title: "Additional feature request",
                description: "Client requested additional reporting functionality",
                requestedBy: "Client",
                requestedDate: new Date(Date.now() - 15 * 86400000).toISOString().split("T")[0],
                status: "approved",
                impactBudget: 15000,
                impactSchedule: 10,
                approvedBy: currentProject.projectManagerId,
                approvedDate: new Date(Date.now() - 10 * 86400000).toISOString().split("T")[0],
              },
              {
                id: mockSeedProjectId * 100 + 2,
                projectId: mockSeedProjectId,
                title: "Scope clarification",
                description: "Clarification on integration requirements",
                requestedBy: "Project Team",
                requestedDate: new Date(Date.now() - 5 * 86400000).toISOString().split("T")[0],
                status: "pending",
                impactBudget: 0,
                impactSchedule: 0,
              },
            ];
            setChangeRequests(mockChangeRequests);

            // Generate comprehensive team member data with mock hours
            const mockTeamMembers: TeamMember[] = projectAllocations.map((alloc) => {
              const employee = employeesData.find((e) => e.id === alloc.employeeId);
              const allocatedHours = Math.floor(Math.random() * 200) + 100; // 100-300 hours
              const actualHours = Math.floor(allocatedHours * (0.3 + Math.random() * 0.8)); // 30-110% of allocated
              const remaining = allocatedHours - actualHours;
              const utilisationPercent = (actualHours / allocatedHours) * 100;

              // Determine status based on utilisation only
              let status: TeamMember["status"];

              if (utilisationPercent > 100) {
                status = "Over Allocated";
              } else if (utilisationPercent > 85) {
                status = "At Risk";
              } else {
                status = "On Track";
              }

              // Generate avatar URL using UI Avatars service
              const name = employee?.name || "Unknown";
              const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&size=128`;

              return {
                id: alloc.id,
                employeeId: alloc.employeeId,
                name: name,
                email: employee?.email || "",
                role: alloc.role,
                avatar: avatarUrl,
                allocatedHours,
                actualHours,
                remaining,
                startDate: alloc.startDate,
                endDate: alloc.endDate,
                status,
                utilisationPercent,
              };
            });

            // Find employees who logged time but weren't allocated
            const demoAllocatedEmployeeIds = new Set(mockTeamMembers.map((tm) => tm.employeeId));
            const demoUnallocatedWithTime: TeamMember[] = [];

            // Group time entries by employee
            const demoTimeByEmployee = new Map<number, number>();
            mockTimeEntries.forEach((entry) => {
              if (!demoAllocatedEmployeeIds.has(entry.employeeId)) {
                const current = demoTimeByEmployee.get(entry.employeeId) || 0;
                demoTimeByEmployee.set(entry.employeeId, current + entry.hours);
              }
            });

            // Create TeamMember entries for unallocated employees who logged time
            demoTimeByEmployee.forEach((hours, employeeId) => {
              const employee = employeesData.find((e) => e.id === employeeId);
              if (employee) {
                const name = employee.name;
                const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&size=128`;

                demoUnallocatedWithTime.push({
                  id: `unalloc-${employeeId}`,
                  employeeId: employee.id,
                  name: name,
                  email: employee.email || "",
                  role: employee.role || "Unassigned", // Use employee.role instead of employee.position
                  avatar: avatarUrl,
                  allocatedHours: 0, // Not allocated
                  actualHours: hours,
                  remaining: -hours, // Negative because they worked without allocation
                  startDate: "", // No allocation dates
                  endDate: "",
                  status: "Over Allocated", // Working without allocation
                  utilisationPercent: 0, // Can't calculate without allocation
                });
              }
            });

            // Combine allocated and unallocated team members
            setTeamMembers([...mockTeamMembers, ...demoUnallocatedWithTime]);
          }

          // Fetch cost centres - use API in live mode, mock data in demo mode
          // Note: dataMode is already defined above
          if (dataMode === "live") {
            setCostCentresLoading(true);
            fetchCostCentreStructureByProjectId(numericProjectId).then((apiCostCentres) => {
              if (ignore) return;
              setCostCentres(apiCostCentres.filter(cc => !cc.isStructureNode));
              setHierarchicalCostCentres(apiCostCentres);
            }).catch((error) => {
              console.error("Failed to fetch cost centres from API:", error);
              if (!ignore) {
                setCostCentres([]);
                setHierarchicalCostCentres([]);
              }
            }).finally(() => {
              if (!ignore) setCostCentresLoading(false);
            });
          } else {
            // Helper arrays for variety (used by both flat and hierarchical views)
            const types = ["Functional Area", "Administrative", "Technical Support", "Operations", "Support Services"];
            const statuses: Array<"Not Started" | "In Progress" | "On Hold" | "Completed" | "Cancelled"> = [
              "Not Started",
              "In Progress",
              "On Hold",
              "Completed",
              "Cancelled",
            ];
            const departments = [
              "Engineering",
              "Design",
              "Quality Assurance",
              "PMO",
              "IT Operations",
              "Marketing",
              "Sales",
              "Finance",
              "HR",
              "Legal",
            ];
            const locations = [
              "London Office",
              "Manchester Office",
              "Birmingham Office",
              "Head Office",
              "Data Centre",
              "Edinburgh Office",
              "Bristol Office",
              "Leeds Office",
              "Remote",
              "Client Site",
            ];
            const priorities = ["High", "Medium", "Low"];
            const owners = [
              "John Smith",
              "Sarah Johnson",
              "Mike Davis",
              "Emily Brown",
              "David Wilson",
              "Alice Chen",
              "Bob Martinez",
              "Carol White",
              "Dan Brown",
              "Emma Wilson",
            ];

            // Mock cost centres data for demo mode - 50 records for scrolling test
            const mockCostCentres: CostCentre[] = [];

            // Generate 50 cost centres
            for (let i = 1; i <= 50; i++) {
              const code = `CC-${String(i).padStart(3, "0")}`;
              const typeIndex = (i - 1) % types.length;
              const statusIndex = (i - 1) % statuses.length;
              const deptIndex = (i - 1) % departments.length;
              const locIndex = (i - 1) % locations.length;
              const priorityIndex = (i - 1) % priorities.length;
              const ownerIndex = (i - 1) % owners.length;

              // Vary the budget allocation
              const budgetFactor = 0.02 * (1 + (i % 5) * 0.5); // Between 2% and 6% of total
              const actualFactor = budgetFactor * (0.85 + Math.random() * 0.3); // 85% to 115% of budget
              const committedFactor = budgetFactor * 0.1;

              mockCostCentres.push({
                id: -Date.now() - i,
                code: code,
                name: `Cost Centre ${i}`,
                description: `Description for cost centre ${code}`,
                type: types[typeIndex],
                status: statuses[statusIndex],
                billable: i % 3 !== 0, // 2/3 are billable
                costsBudget: currentProject.budgetCost * budgetFactor,
                actualCosts: currentProject.actualCost * actualFactor,
                committedCosts: currentProject.budgetCost * committedFactor,
                revenueBudget: currentProject.budgetRevenue * budgetFactor,
                actualRevenue: currentProject.actualRevenue * actualFactor * 0.95,
                hoursBudget: Math.round(budgetFactor * 10000),
                actualHours: Math.round(actualFactor * 9500),
                owner: owners[ownerIndex],
                lastUpdated: new Date(Date.now() - (i % 10) * 86400000),
                createdAt: new Date(currentProject.startDate),
                updatedAt: new Date(Date.now() - (i % 10) * 86400000),
                customFields: {
                  Department: departments[deptIndex],
                  Location: locations[locIndex],
                  Priority: priorities[priorityIndex],
                  "Start Date": new Date(currentProject.startDate),
                  "End Date": new Date(currentProject.endDate),
                  "Requires Approval": i % 2 === 0,
                  "Approval Threshold": 1000 + i * 100,
                  "Cost Code": `${code}-${departments[deptIndex].substring(0, 3).toUpperCase()}`,
                  "GL Account": `${6100 + (i % 5) * 100}-${1000 + (i % 10) * 100}`,
                  "Project Phase": `Phase ${(i % 5) + 1}`,
                },
              });
            }
            setCostCentres(mockCostCentres);

            // Mock hierarchical cost centres data - includes all 50 cost centre codes
            const mockHierarchicalCostCentres: HierarchicalCostCentre[] = [];

            // Create 5 parent departments, each with 10 cost centres
            const parentDepartments: Array<{ code: string; name: string; range: [number, number] }> = [
              { code: "CC-DEPT-01", name: "Engineering Department", range: [1, 10] },
              { code: "CC-DEPT-02", name: "Design & Creative", range: [11, 20] },
              { code: "CC-DEPT-03", name: "Operations", range: [21, 30] },
              { code: "CC-DEPT-04", name: "Support Services", range: [31, 40] },
              { code: "CC-DEPT-05", name: "Administration", range: [41, 50] },
            ];

            parentDepartments.forEach((dept, deptIndex) => {
              // Calculate totals for parent
              const deptBudgetFactor = 0.2; // Each department gets 20% of total budget
              // Use a stable, fixed parent ID so children can reference it reliably
              const parentId = -(1000 + deptIndex);

              // Add parent department
              mockHierarchicalCostCentres.push({
                id: parentId,
                code: dept.code,
                name: dept.name,
                billable: true,
                costsBudget: currentProject.budgetCost * deptBudgetFactor,
                actualCosts: currentProject.actualCost * deptBudgetFactor * (0.9 + Math.random() * 0.2),
                committedCosts: currentProject.budgetCost * deptBudgetFactor * 0.05,
                revenueBudget: currentProject.budgetRevenue * deptBudgetFactor,
                actualRevenue: currentProject.actualRevenue * deptBudgetFactor * (0.9 + Math.random() * 0.2),
                hoursBudget: Math.round(deptBudgetFactor * 10000),
                actualHours: Math.round(deptBudgetFactor * 9500 * (0.9 + Math.random() * 0.2)),
                parentId: null,
              });

              // Add child cost centres (CC-001 to CC-050)
              for (let i = dept.range[0]; i <= dept.range[1]; i++) {
                const code = `CC-${String(i).padStart(3, "0")}`;
                const budgetFactor = 0.02 * (1 + (i % 5) * 0.5); // Between 2% and 6% of total
                const actualFactor = budgetFactor * (0.85 + Math.random() * 0.3);
                const committedFactor = budgetFactor * 0.1;

                const statusIndex = (i - 1) % statuses.length;
                const ownerIndex = (i - 1) % owners.length;

                mockHierarchicalCostCentres.push({
                  id: -(2000 + i),
                  code: code,
                  name: `Cost Centre ${i}`,
                  type: types[(i - 1) % types.length],
                  status: statuses[statusIndex],
                  billable: i % 3 !== 0,
                  costsBudget: currentProject.budgetCost * budgetFactor,
                  actualCosts: currentProject.actualCost * actualFactor,
                  committedCosts: currentProject.budgetCost * committedFactor,
                  revenueBudget: currentProject.budgetRevenue * budgetFactor,
                  actualRevenue: currentProject.actualRevenue * actualFactor * 0.95,
                  hoursBudget: Math.round(budgetFactor * 10000),
                  actualHours: Math.round(actualFactor * 9500),
                  owner: owners[ownerIndex],
                  lastUpdated: new Date(Date.now() - (i % 10) * 86400000),
                  parentId: parentId,
                });
              }
            });

            setHierarchicalCostCentres(mockHierarchicalCostCentres);
            setCostCentresLoading(false);
          }
        } else {
          setLoading(false);
          setTimeEntriesLoading(false);
          setCostCentresLoading(false);
        }

      } catch (error) {
        if (!ignore) {
          handleApiError(error, `/api/projects/${projectId}`, "GET");
          setLoading(false);
          setTimeEntriesLoading(false);
          setCostCentresLoading(false);
        }
      }
    }
    loadData();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, dataMode]);


  // Reset lazy-load flag when data mode changes so billing re-fetches after a toggle
  const billingDataLoadedRef = useRef(false);
  const prevDataModeRef = useRef(dataMode);
  if (prevDataModeRef.current !== dataMode) {
    prevDataModeRef.current = dataMode;
    billingDataLoadedRef.current = false;
  }

  // Lazy-load billing data when Billing, Financials, or Overview tabs are activated
  // (Invoice Summary on Financials/Overview needs billing invoice data)
  useEffect(() => {
    if (activeTab !== "billing" && activeTab !== "financials" && activeTab !== "overview") return;
    if (loading || !project) return;
    const dataMode = getDataMode();
    if (dataMode !== "live") return;
    // Only fetch on first activation; subsequent refreshes use refetchBillingData
    if (billingDataLoadedRef.current) return;
    billingDataLoadedRef.current = true;

    async function fetchBilling() {
      try {
        setBillingLoading(true);

        // Sync costing transactions for fixed-price projects so that
        // "Based on Approved Time" completion % picks up hours from FinOps.
        const pType = String(project?.type ?? '').toLowerCase();
        if (pType === 'fixed-price' && project?.code && currentCompany) {
          try {
            await triggerCostingSync(
              project.code,
              numericProjectId,
              currentCompany.id,
              undefined,
              currentCompany.id,
            );
            const maxPolls = 30;
            for (let i = 0; i < maxPolls; i++) {
              await new Promise(r => setTimeout(r, 1000));
              const syncStatus = await getCostingSyncStatus(numericProjectId);
              if (syncStatus.status === 'completed' || syncStatus.status === 'failed') break;
            }
            // Re-fetch budget data so completion % reflects the synced hours
            refetchBudget();
          } catch (syncErr) {
            console.warn('Fixed-price costing sync failed (non-blocking):', syncErr);
          }
        }

        // Trigger payment status sync before fetching invoices
        // so the user sees up-to-date payment statuses
        try {
          await refreshInvoicePaymentStatus();
        } catch (syncErr) {
          console.warn("Payment status refresh failed (non-blocking):", syncErr);
        }
        const [apiBillingWipResponse, apiBillingInvoiceResponse] = await Promise.all([
          fetchBillingWipItemsPaged(numericProjectId, 0, 1, wipItemsPerPage),
          fetchBillingInvoicesPaged(numericProjectId, 1, invoiceItemsPerPage),
        ]);
        setBillingWipItems(apiBillingWipResponse.items);
        setWipTotalCount(apiBillingWipResponse.totalCount);
        if (apiBillingWipResponse.summary) setWipSummary(apiBillingWipResponse.summary);
        setBillingInvoices(apiBillingInvoiceResponse.items);
        setInvoiceTotalCount(apiBillingInvoiceResponse.totalCount);
        setInvoiceTotalInvoiced(apiBillingInvoiceResponse.totalInvoiced ?? 0);
        setInvoiceTotalInvoicedNet(apiBillingInvoiceResponse.totalInvoicedNet ?? 0);
        setInvoiceTotalOutstanding(apiBillingInvoiceResponse.totalOutstanding ?? 0);
        setInvoiceTotalPaid(apiBillingInvoiceResponse.totalPaid ?? 0);
      } catch (billingError) {
        console.error("Failed to fetch billing data from API:", billingError);
        setBillingWipItems([]);
        setWipTotalCount(0);
        setWipSummary({ totalNetAmount: 0, timeNetAmount: 0, expenseNetAmount: 0, timeHours: 0 });
        setBillingInvoices([]);
        setInvoiceTotalCount(0);
      } finally {
        setBillingLoading(false);
      }
    }
    fetchBilling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, loading, project]);

  // Re-fetch invoice page when pagination, search, or status filter changes
  const invoicePageFetchedRef = useRef(false);
  useEffect(() => {
    // Skip the very first render — initial data is loaded by fetchBilling above
    if (!invoicePageFetchedRef.current) {
      invoicePageFetchedRef.current = true;
      return;
    }
    if (!billingDataLoadedRef.current) return;
    const dataMode = getDataMode();
    if (dataMode !== "live") return;

    let cancelled = false;
    async function fetchInvoicePage() {
      try {
        setBillingLoading(true);
        const statusParam = invoiceStatusFilter !== "all"
          ? ({ Draft: 0, Sent: 1, PartiallyPaid: 2, Paid: 3, Overdue: 4 } as Record<string, number>)[invoiceStatusFilter]
          : undefined;
        const response = await fetchBillingInvoicesPaged(
          numericProjectId, invoiceCurrentPage, invoiceItemsPerPage, statusParam, invoiceSearchQuery || undefined
        );
        if (!cancelled) {
          setBillingInvoices(response.items);
          setInvoiceTotalCount(response.totalCount);
          setInvoiceTotalInvoiced(response.totalInvoiced ?? 0);
          setInvoiceTotalInvoicedNet(response.totalInvoicedNet ?? 0);
          setInvoiceTotalOutstanding(response.totalOutstanding ?? 0);
          setInvoiceTotalPaid(response.totalPaid ?? 0);
        }
      } catch (error) {
        console.error("Failed to fetch invoice page:", error);
      } finally {
        if (!cancelled) setBillingLoading(false);
      }
    }
    fetchInvoicePage();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceCurrentPage, invoiceItemsPerPage, invoiceStatusFilter, invoiceSearchQuery]);

  // Batch-check which invoices already have a stored PDF (single request instead of N)
  useEffect(() => {
    if (billingInvoices.length === 0) { setPdfExistsSet(new Set()); return; }
    let cancelled = false;
    const ids = billingInvoices.map((inv) => inv.id);
    hasStoredPdfBatch(ids).then((set) => { if (!cancelled) setPdfExistsSet(set); });
    return () => { cancelled = true; };
  }, [billingInvoices]);

  // Billing action handlers
  const refetchBillingData = useCallback(async () => {
    if (dataMode !== "live") return;
    try {
      setBillingLoading(true);
      const statusParam = invoiceStatusFilter !== "all"
        ? ({ Draft: 0, Sent: 1, PartiallyPaid: 2, Paid: 3, Overdue: 4 } as Record<string, number>)[invoiceStatusFilter]
        : undefined;
      const [updatedWipResponse, updatedInvoiceResponse] = await Promise.all([
        fetchBillingWipItemsPaged(numericProjectId, 0, wipCurrentPage, wipItemsPerPage),
        fetchBillingInvoicesPaged(numericProjectId, invoiceCurrentPage, invoiceItemsPerPage, statusParam, invoiceSearchQuery || undefined),
      ]);
      setBillingWipItems(updatedWipResponse.items);
      setWipTotalCount(updatedWipResponse.totalCount);
      if (updatedWipResponse.summary) setWipSummary(updatedWipResponse.summary);
      setBillingInvoices(updatedInvoiceResponse.items);
      setInvoiceTotalCount(updatedInvoiceResponse.totalCount);
      setInvoiceTotalInvoiced(updatedInvoiceResponse.totalInvoiced ?? 0);
      setInvoiceTotalInvoicedNet(updatedInvoiceResponse.totalInvoicedNet ?? 0);
      setInvoiceTotalOutstanding(updatedInvoiceResponse.totalOutstanding ?? 0);
      setInvoiceTotalPaid(updatedInvoiceResponse.totalPaid ?? 0);
    } catch (error) {
      console.error("Failed to refresh billing data:", error);
    } finally {
      setBillingLoading(false);
    }
  }, [dataMode, numericProjectId, wipCurrentPage, wipItemsPerPage, invoiceCurrentPage, invoiceItemsPerPage, invoiceStatusFilter, invoiceSearchQuery]);

  const handleCreateDraft = useCallback(async (description: string, notes?: string) => {
    if (dataMode !== "live") return;
    // Get selected WIP item IDs (extract numeric ID from "wip-{id}" format)
    const wipItemIds = selectedWipItems
      .map((id) => {
        const numId = parseInt(id.replace("wip-", ""), 10);
        return isNaN(numId) ? null : numId;
      })
      .filter((id): id is number => id !== null);
    
    if (wipItemIds.length === 0) {
      console.warn("No WIP items selected for draft creation");
      return;
    }

    try {
      setBillingActionLoading(true);
      await createBillingDraft(numericProjectId, wipItemIds, description, notes);
      setSelectedWipItems([]); // Clear selection
      setCreateDraftDialogOpen(false);
      await refetchBillingData();
    } catch (error) {
      console.error("Failed to create draft:", error);
      handleApiError(error, `/api/billing/${numericProjectId}/drafts`, "POST");
    } finally {
      setBillingActionLoading(false);
    }
  }, [dataMode, numericProjectId, selectedWipItems, refetchBillingData, handleApiError]);

  const handleDeleteDraft = useCallback(async (invoiceId: number) => {
    if (dataMode !== "live") return;
    try {
      setBillingActionLoading(true);
      await deleteBillingDraft(invoiceId);
      await refetchBillingData();
    } catch (error) {
      console.error("Failed to delete draft:", error);
      handleApiError(error, `/api/billing/drafts/${invoiceId}`, "DELETE");
    } finally {
      setBillingActionLoading(false);
    }
  }, [dataMode, refetchBillingData, handleApiError]);

  // Edit-Draft mode — open the Invoice Generation Wizard pre-populated with a
  // saved draft invoice's lines & values (Draft row "Edit" action).
  const handleEditDraftInvoice = useCallback(async (invoiceId: number) => {
    try {
      setEditDraftLoading(true);
      const { invoice, lineItems } = await fetchBillingInvoiceById(invoiceId);
      const payload: EditDraftData = {
        invoiceId: invoice.id,
        invoiceDate: invoice.invoiceDate,
        dueDate: invoice.dueDate,
        description: invoice.description,
        notes: invoice.notes,
        invoiceNumber: invoice.invoiceNumber,
        emailDraft: (invoice.draftEmailTo || invoice.draftEmailCc || invoice.draftEmailBcc ||
                     invoice.draftEmailSubject || invoice.draftEmailBody || invoice.draftEmailTemplateId)
          ? {
              to: invoice.draftEmailTo,
              cc: invoice.draftEmailCc,
              bcc: invoice.draftEmailBcc,
              subject: invoice.draftEmailSubject,
              body: invoice.draftEmailBody,
              templateId: invoice.draftEmailTemplateId,
            }
          : undefined,
        lineItems: lineItems.map((li) => ({
          wipLedgerId: li.wipLedgerId,
          description: li.description,
          quantity: li.quantity,
          rate: li.rate,
          netAmount: li.netAmount,
          date: li.wipDate,
          resource: li.wipResourceName,
          role: li.wipRoleName,
        })),
      };
      setEditDraftData(payload);
      setInvoiceWizardOpen(true);
    } catch (error) {
      console.error("Failed to load draft invoice for editing:", error);
      handleApiError(error, `/api/billing/invoices/${invoiceId}`, "GET");
    } finally {
      setEditDraftLoading(false);
    }
  }, [handleApiError]);

  const handlePostToFinancials = useCallback(async (invoiceId: number) => {
    if (dataMode !== "live") return;
    try {
      setBillingActionLoading(true);
      const result = await postBillingDraftToFinancials(invoiceId);

      if (result.success) {
        toast.success("Invoice posted to Access Financials", {
          description: result.financialsIds?.length
            ? `Transaction ID${result.financialsIds.length > 1 ? "s" : ""}: ${result.financialsIds.join(", ")}`
            : undefined,
        });

        // Auto-generate PDF and store against the invoice
        try {
          const { invoice: postedInvoice, lineItems: postedLineItems } = await fetchBillingInvoiceById(invoiceId);
          const pdfTransaction: InvoicePdfTransaction = {
            invoiceNumber: postedInvoice.invoiceNumber ?? `#${postedInvoice.id}`,
            date: postedInvoice.invoiceDate ? toLocalDateString(new Date(postedInvoice.invoiceDate)) : toLocalDateString(new Date()),
            dueDate: postedInvoice.dueDate ? toLocalDateString(new Date(postedInvoice.dueDate)) : undefined,
            status: "Sent",
            description: postedInvoice.description || "",
            amount: postedInvoice.grossTotal ?? 0,
            projectName: project?.name,
            accountName: project?.clientNames,
            projectDescription: project?.description || '',
            projectManager: projectManager?.name || '',
          };
          const companyDetails = await fetchFullCompanyDetails(currentCompany);
          const pdfResult = await generateAndStoreInvoicePdf(pdfTransaction, invoiceId, postedLineItems, companyDetails, undefined, undefined, projectSettings.invoiceTemplateId, postedInvoice.notes || undefined);

          // Upload PDF to ADM using user's token (browser-side)
          try {
            const admCtx = await getDocumentContext();
            if (admCtx) {
              const admDocId = await uploadInvoicePdfToAdm(admCtx, getAdmAccessToken, pdfResult.pdfBlob, {
                invoiceNumber: postedInvoice.invoiceNumber ?? `#${postedInvoice.id}`,
                documentDate: postedInvoice.invoiceDate ? toLocalDateString(new Date(postedInvoice.invoiceDate)) : toLocalDateString(new Date()),
                description: postedInvoice.description || "",
                accountName: project?.clientNames,
                transactionType: "Invoice",
              });
              await storeAdmDocumentId(invoiceId, admDocId);
            }
          } catch (admErr) {
            console.warn("ADM upload failed (non-blocking):", admErr);
            toast.warning("Document upload to ADM failed", {
              description: admErr instanceof Error ? admErr.message : "The invoice was posted but the document was not uploaded to Access Document Management.",
            });
          }
        } catch (pdfErr) {
          console.error("Auto PDF generation failed (non-blocking):", pdfErr);
        }

        await refetchBillingData();
      } else if (result.validationErrors && result.validationErrors.length > 0) {
        const errorMessages = result.validationErrors
          .map((e) => {
            const details = e.details?.map((d) => `${d.field}: ${d.value}`).join(", ");
            return details ? `${e.message} (${details})` : e.message;
          })
          .join("\n");
        toast.error("Financials validation failed", {
          description: errorMessages,
          duration: 10000,
        });
      } else {
        toast.error("Failed to post invoice to Financials", {
          description: result.error || "An unexpected error occurred",
          duration: 8000,
        });
      }
    } catch (error: any) {
      console.error("Failed to post to financials:", error);
      const errorMsg = error?.message || error?.metadata?.apiError?.error || "";
      if (errorMsg.includes("has no line items") || errorMsg.includes("already been invoiced")) {
        toast.error("Invoice could not be posted", {
          description: "This invoice has no line items. The items may have already been invoiced by another user or process. Please refresh the page and try again.",
          duration: 8000,
        });
      } else {
        handleApiError(error, `/api/billing/drafts/${invoiceId}/post`, "POST");
      }
    } finally {
      setBillingActionLoading(false);
    }
  }, [dataMode, project, currentCompany, projectSettings.invoiceTemplateId, refetchBillingData, handleApiError, getDocumentContext, getAdmAccessToken]);

  const handleVoidInvoice = useCallback(async (invoiceId: number, reason: string) => {
    if (dataMode !== "live") return;
    try {
      setBillingActionLoading(true);
      await voidBillingInvoice(invoiceId, reason);
      await refetchBillingData();
    } catch (error) {
      console.error("Failed to void invoice:", error);
      handleApiError(error, `/api/billing/invoices/${invoiceId}/void`, "POST");
    } finally {
      setBillingActionLoading(false);
    }
  }, [dataMode, refetchBillingData, handleApiError]);

  // Legacy event listener (second instance) — no longer triggered from UI
  useEffect(() => {
    const handleEditMode = (event: Event) => {
      const customEvent = event as CustomEvent<{ projectId: string }>;
      if (customEvent.detail?.projectId === projectId) {
        handleEnterEditMode();
      }
    };

    window.addEventListener("project-edit-mode", handleEditMode);
    return () => window.removeEventListener("project-edit-mode", handleEditMode);
  }, [projectId, handleEnterEditMode]);

  // Load recent client IDs from localStorage
  useEffect(() => {
    const storedRecentClients = localStorage.getItem("recentClientIds");
    const storedRecentPMs = localStorage.getItem("recentProjectManagerIds");
    const storedRecentAMs = localStorage.getItem("recentAccountManagerIds");
    if (storedRecentPMs) {
      try {
        const parsed = JSON.parse(storedRecentPMs);
        setRecentProjectManagerIds(Array.isArray(parsed) ? parsed : []);
      } catch (error) {
        console.error("Failed to parse recent project manager IDs:", error);
        setRecentProjectManagerIds([]);
      }
    }
    if (storedRecentAMs) {
      try {
        const parsed = JSON.parse(storedRecentAMs);
        setRecentAccountManagerIds(Array.isArray(parsed) ? parsed : []);
      } catch (error) {
        console.error("Failed to parse recent account manager IDs:", error);
        setRecentAccountManagerIds([]);
      }
    }
    if (storedRecentClients) {
      try {
        const parsed = JSON.parse(storedRecentClients);
        setRecentClientIds(Array.isArray(parsed) ? parsed : []);
      } catch (error) {
        console.error("Failed to parse recent client IDs:", error);
        setRecentClientIds([]);
      }
    }
  }, []);

  // Helper functions (originally defined after loading check)
  // These are safe because they gracefully handle null project via optional chaining
  const getEmployeeName = (employeeId: number) => {
    return employees.find((e) => e.id === employeeId)?.name || "Unknown";
  };

  const getStatusBadgeVariant = (
    status: TeamMember["status"],
    utilisation?: number,
  ): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case "On Track":
        // Use different colours based on utilisation
        if (utilisation !== undefined) {
          if (utilisation > 75) {
            return "default"; // Blue for higher utilisation (75-85%)
          } else if (utilisation > 50) {
            return "outline"; // Outline for medium utilisation (50-75%)
          } else {
            return "secondary"; // Gray for low utilisation (0-50%)
          }
        }
        return "default";
      case "At Risk":
        return "destructive";
      case "Over Allocated":
        return "destructive";
      default:
        return "default";
    }
  };

  const getStatusColor = (status: TeamMember["status"] | string): string => {
    // Normalize status to lowercase for comparison
    const normalizedStatus = status.toLowerCase();
    switch (normalizedStatus) {
      case "completed":
        return "text-neutral-600";
      case "on track":
      case "on-track":
        return "text-success-600";
      case "active":
      case "in-progress":
        return "text-info-600";
      case "at risk":
      case "at-risk":
        return "text-warning-600";
      case "over allocated":
      case "over-allocated":
        return "text-error-600";
      case "planning":
      case "on-hold":
        return "text-neutral-500";
      default:
        return "text-neutral-600";
    }
  };

  /**
   * Shared assignment handler used by BOTH the Team tab and the Resource Budget tab.
   * Creates resource allocations / person budgets (live mode), updates the resource
   * budget grid (byRole/byPerson) and the Team tab roster so both tabs stay in sync.
   */
  const handleAssignResources = async (role: string, selections: AssignmentSelection[]) => {
    const dataMode = getDataMode();

    // Calculate total hours being assigned
    const totalAssignedHours = selections.reduce((sum, s) => sum + s.allocatedHours, 0);

    // In live mode, create resource allocations via API
    const createdAllocationIds: Map<number, string> = new Map();
    if (dataMode === "live") {
      try {
        const now = new Date();
        const startDate = project?.startDate || now.toISOString().split("T")[0];
        const endDate =
          project?.endDate ||
          new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

        // Create resource allocations for each selection
        for (const selection of selections) {
          const allocationData = {
            projectId: numericProjectId,
            employeeId: selection.personId,
            role: role,
            allocatedHours: selection.allocatedHours,
            startDate: startDate,
            endDate: endDate,
            status: "confirmed" as const,
          };

          const createdAllocation = await createResourceAllocation(allocationData);
          createdAllocationIds.set(selection.personId, createdAllocation.id);
        }
        // Also create PersonBudget records so Rate Card can show assigned people
        let resourceBudgetId = enrichedBudgetData?.resourceBudget?.id;
        if (!resourceBudgetId) {
          console.warn(
            "[handleAssignResources] resourceBudgetId not found in local state, fetching from API...",
          );
          resourceBudgetId = (await fetchResourceBudgetIdForProject(numericProjectId)) ?? undefined;
        }
        if (resourceBudgetId) {
          const matchedRole = enrichedBudgetData?.resourceBudget?.byRole?.find(
            (r: RoleBudget) => r.role === role,
          );
          for (const selection of selections) {
            const employee = employees.find((e: Employee) => e.id === selection.personId);
            try {
              await createPersonBudget({
                resourceBudgetId,
                personId: selection.personId,
                personName: employee?.name || selection.personName,
                roleId: matchedRole?.roleId,
                budgetHours: selection.allocatedHours,
                costRate: 0,
              });
            } catch (pbError) {
              console.warn("Failed to create PersonBudget record:", pbError);
            }
          }
        } else {
          console.warn(
            "[handleAssignResources] No resourceBudgetId available — skipping PersonBudget creation",
          );
        }
      } catch (error) {
        console.error("Failed to create resource allocations:", error);
        toast.error("Failed to assign team members to project");
        return;
      }
    }

    // Update budget data to reflect the new assignments
    if (enrichedBudgetData?.resourceBudget?.byRole) {
      const updatedRoles = enrichedBudgetData.resourceBudget.byRole.map((r: RoleBudget) => {
        if (r.role === role) {
          return {
            ...r,
            allocatedHours: r.allocatedHours + totalAssignedHours,
          };
        }
        return r;
      });

      const newPersonBudgets = selections.map((selection) => {
        const employee = employees.find((e: Employee) => e.id === selection.personId);
        return {
          id: -Date.now() - selection.personId,
          personId: selection.personId,
          personName: employee?.name || selection.personName,
          role: role,
          budgetHours: selection.allocatedHours,
          actualHours: 0,
          utilisation: 0,
          costRate: 0,
          budgetCost: 0,
          actualCost: 0,
        };
      });

      const existingByPerson = enrichedBudgetData.resourceBudget.byPerson || [];

      setLocalBudgetData({
        ...enrichedBudgetData,
        resourceBudget: {
          ...enrichedBudgetData.resourceBudget,
          byRole: updatedRoles,
          byPerson: [...existingByPerson, ...newPersonBudgets],
        },
      });
    }

    // Update team members - either add new or update existing
    setTeamMembers((prev: TeamMember[]) => {
      const updatedMembers = [...prev];
      const newMembers: TeamMember[] = [];

      selections.forEach((selection) => {
        // Check if team member already exists (by employeeId)
        const existingIndex = updatedMembers.findIndex((tm) => tm.employeeId === selection.personId);

        const employee = employees.find((e: Employee) => e.id === selection.personId);
        const now = new Date();
        const startDate = project?.startDate || now.toISOString();
        const endDate =
          project?.endDate || new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString();

        if (existingIndex !== -1) {
          // Team member already exists - update their allocated hours
          const existing = updatedMembers[existingIndex];
          const newAllocatedHours = existing.allocatedHours + selection.allocatedHours;
          const remaining = newAllocatedHours - existing.actualHours;
          const utilisationPercent = (existing.actualHours / newAllocatedHours) * 100;

          // Determine updated status
          let status: TeamMember["status"];
          if (utilisationPercent > 100) {
            status = "Over Allocated";
          } else if (utilisationPercent > 85) {
            status = "At Risk";
          } else {
            status = "On Track";
          }

          // Update existing team member
          updatedMembers[existingIndex] = {
            ...existing,
            allocatedHours: newAllocatedHours,
            remaining,
            utilisationPercent,
            status,
          };
        } else {
          // Team member doesn't exist - create new entry
          // In live mode, actual hours start at 0; in demo mode, generate mock data
          const actualHours =
            dataMode === "live"
              ? 0
              : Math.floor(selection.allocatedHours * (0.3 + Math.random() * 0.4));
          const remaining = selection.allocatedHours - actualHours;
          const utilisationPercent =
            selection.allocatedHours > 0 ? (actualHours / selection.allocatedHours) * 100 : 0;

          // Determine status
          let status: TeamMember["status"];
          if (utilisationPercent > 100) {
            status = "Over Allocated";
          } else if (utilisationPercent > 85) {
            status = "At Risk";
          } else {
            status = "On Track";
          }

          // Generate avatar URL
          const name = employee?.name || selection.personName;
          const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&size=128`;

          // Use the created allocation ID in live mode, or generate a temp ID in demo mode
          const allocationId =
            createdAllocationIds.get(selection.personId) ||
            `tm-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

          newMembers.push({
            id: allocationId,
            employeeId: selection.personId,
            name: name,
            email: employee?.email || "",
            role: employee?.role || role, // Use employee's actual role, fallback to unfulfilled role
            avatar: avatarUrl,
            allocatedHours: selection.allocatedHours,
            actualHours,
            remaining,
            startDate,
            endDate,
            status,
            utilisationPercent,
          });
        }
      });

      // Return updated members plus any new members
      return [...updatedMembers, ...newMembers];
    });

    toast.success(
      `Assigned ${selections.length} team member${selections.length !== 1 ? "s" : ""} to ${role} role`,
      {
        description: selections.map((s) => `${s.personName}: ${s.allocatedHours}h`).join(", "),
      },
    );
  };

  /**
   * Refetches allocations and rebuilds the Team roster. The Team tab's booking-status
   * request reconciles Scheduler bookings into allocations server-side, so the
   * allocations loaded on mount can be stale by the time it returns.
   */
  const refreshTeamAllocations = useCallback(async () => {
    if (!numericProjectId || getDataMode() !== 'live') return;

    try {
      const allocations = await fetchResourceAllocations("projects");
      const projectAllocations = allocations.filter((r: ResourceAllocation) => r.projectId === numericProjectId);
      setAllResourceAllocations(allocations);
      setResourceAllocations(projectAllocations);
      setTeamMembers(buildTeamMembers(
        employees,
        projectAllocations,
        buildApprovedHoursByEmployee(timeEntries),
        excludedTeamEmployeeIds,
      ));
    } catch (err) {
      console.error('[Team] Failed to refresh resource allocations:', err);
    }
  }, [numericProjectId, employees, timeEntries, excludedTeamEmployeeIds]);

  const handleRemoveTeamMember = async (member: TeamMember) => {
    // Only allow removal if no hours have been logged
    if (member.actualHours > 0) {
      toast.error("Cannot remove team member who has logged hours");
      return;
    }

    const dataMode = getDataMode();

    if (dataMode === "live") {
      // Delete the resource allocation if one exists
      if (member.id && member.id !== member.employeeId) {
        try {
          const memberId = typeof member.id === "number" ? member.id : parseInt(String(member.id), 10);
          await deleteResourceAllocation(memberId);
        } catch (error) {
          console.error("Failed to delete resource allocation:", error);
          toast.error("Failed to remove team member from project");
          return;
        }
      }

      // Persist the exclusion so the member stays removed on reload
      if (numericProjectId && member.employeeId) {
        try {
          await createTeamExclusion(numericProjectId, member.employeeId);
        } catch (error) {
          console.error("Failed to persist team exclusion:", error);
          toast.error("Failed to remove team member from project");
          return;
        }
      }
    }

    // Remove the team member from local state
    setTeamMembers((prev) => prev.filter((tm) => tm.id !== member.id));

    // Update the budget data to reflect the removed allocation
    if (enrichedBudgetData?.resourceBudget?.byRole && member.allocatedHours > 0) {
      const updatedRoles = enrichedBudgetData.resourceBudget.byRole.map((r) => {
        if (r.role === member.role) {
          return {
            ...r,
            allocatedHours: Math.max(0, r.allocatedHours - member.allocatedHours),
          };
        }
        return r;
      });

      // Update local budget data
      setLocalBudgetData({
        ...enrichedBudgetData,
        resourceBudget: {
          ...enrichedBudgetData.resourceBudget,
          byRole: updatedRoles,
        },
      });
    }

    toast.success(`${member.name} removed from project`);
  };

  const getInitials = (name: string): string => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const totalBillableHours = timeEntries.filter((t) => t.billable).reduce((sum, t) => sum + t.hours, 0);

  const totalNonBillableHours = timeEntries.filter((t) => !t.billable).reduce((sum, t) => sum + t.hours, 0);

  // Realisation Rate — used for project health Dimension 7
  // = (actual billed revenue / potential billable value) × 100
  // Potential billable value = SUM(approved billable entries × billRate)
  // Only available when timesheet entries are loaded; null when no approved billable entries exist.
  const _billableApprovedEntries = timeEntries.filter((t) => t.billable && t.approved);
  const _potentialBillableValue = _billableApprovedEntries.reduce((sum, t) => sum + t.hours * t.billRate, 0);
  const realisationRatePct: number | null =
    _billableApprovedEntries.length > 0 && _potentialBillableValue > 0
      ? Math.round(((project?.actualSales ?? 0) / _potentialBillableValue) * 100)
      : null;

  const totalExpenseAmount = expenses.reduce((sum, e) => sum + e.amount, 0);

  const completedMilestones = milestones.filter((m) => m.status === "completed").length;
  const overdueMilestones = milestones.filter((m) => m.status === "overdue").length;

  const activeResources = resourceAllocations.filter((r) => r.status === "confirmed").length;

  const pendingChangeRequests = changeRequests.filter((cr) => cr.status === "pending").length;
  const approvedChangeRequests = changeRequests.filter((cr) => cr.status === "approved").length;

  // Create default empty budget data for new projects that don't have budget data yet
  const getDefaultBudgetData = (): BudgetData => ({
    projectId: numericProjectId,
    projectName: project?.name || "New Project",
    projectType: (project?.type as "fixed-price" | "time-and-materials" | "retainer") ?? "fixed-price",
    summary: {
      revenue: {
        budget: 0,
        actual: 0,
        variance: 0,
        variancePercent: 0,
        progressPercent: 0,
        trend: "stable",
        status: "green",
      },
      costs: {
        budget: 0,
        actual: 0,
        variance: 0,
        variancePercent: 0,
        progressPercent: 0,
        trend: "stable",
        status: "green",
      },
      margin: { budgetedMargin: 0, actualMargin: 0, variance: 0, status: "green" },
      hours: { budget: 0, actual: 0, remaining: 0, progressPercent: 0, status: "green" },
      health: { score: "green", statusText: "On Track", factors: [] },
    },
    costBudget: {
      projectId: numericProjectId,
      categories: [],
      phases: [],
      totals: { budget: 0, actual: 0, committed: 0, available: 0, progressPercent: 0 },
    },
    salesBudget: {
      projectId: numericProjectId,
      projectType: (project?.type as "fixed-price" | "time-and-materials" | "retainer") ?? "fixed-price",
    },
    resourceBudget: {
      projectId: numericProjectId,
      summary: {
        totalBudget: 0,
        totalActual: 0,
        totalRemaining: 0,
        progressPercent: 0,
        billablePercent: 0,
        targetBillablePercent: 80,
        totalBudgetCost: 0,
        totalActualCost: 0,
      },
      byRole: [],
      byPerson: [],
      byPhase: [],
    },
    allBudgets: {
      projectId: numericProjectId,
      profitability: {
        revenue: { budget: 0, actual: 0, variance: 0 },
        directCosts: { budget: 0, actual: 0, variance: 0 },
        grossProfit: { budget: 0, actual: 0, variance: 0 },
        grossMargin: { budget: 0, actual: 0, variance: 0 },
        overhead: { budget: 0, actual: 0, variance: 0 },
        netProfit: { budget: 0, actual: 0, variance: 0 },
        netMargin: { budget: 0, actual: 0, variance: 0 },
      },
      burnRateData: [],
      forecast: {
        projectedFinalRevenue: 0,
        projectedFinalCost: 0,
        projectedFinalMargin: 0,
        revenueVariance: 0,
        costVariance: 0,
        marginVariance: 0,
      },
    },
    alerts: [],
    revisions: [],
    lastUpdated: new Date().toISOString(),
  });

  // Return ALL state and handlers for the context
  return {
    // Core entities
    project, setProject, client, clients, employees, assignableEmployees, projectManager, accountManager, setAccountManager,
    projectId, numericProjectId, mockSeedProjectId, navigate,
    statusLabels, selectableStatuses,
    schedulerConnected,
    authenticatedUser,
    activePanelClientId, openClientPanel, closeClientPanel,

    // Tab state
    activeTab, setActiveTab,

    // Budget
    budgetData, budgetLoading, budgetError, refetchBudget,
    enrichedBudgetData, localBudgetData, setLocalBudgetData,
    isBudgetingEnabled, budgetPermissions, isResourceBudgetEnabled,
    resourceSettings, updateBudget, updateBudgetLoading,

    // Team
    teamMembers, setTeamMembers,
    handleAssignResources, refreshTeamAllocations,
    sortedTeamMembers, teamPagination, paginatedTeamMembers,
    teamColumnSorts, setTeamColumnSorts, teamPage, setTeamPage, teamPageSize, setTeamPageSize,

    // Collections
    milestones, setMilestones, changeRequests, setChangeRequests,
    timeEntries, expenses, resourceAllocations, allResourceAllocations,

    // Scheduling
    schedulingBookings, schedulingLoading, schedulingError,
    schedulingConnectionStatus, schedulingSchedulerName, schedulerUrl,
    liveResourceRequests, resourceRequestsLoading, pendingRequestRoles,
    liveRoleCoverage, setLiveRoleCoverage,
    raiseRequestModalOpen, setRaiseRequestModalOpen,
    raiseRequestPrefilledRole, setRaiseRequestPrefilledRole,
    raiseRequestCostCentre, setRaiseRequestCostCentre,
    handleRequestSubmitSuccess,

    // Billing
    billingWipItems, billingInvoices, setBillingInvoices, billingLoading, billingActionLoading, setBillingActionLoading,
    pdfExistsSet, wipTotalCount, wipSummary,
    billingSubTab, setBillingSubTab,
    fixedFeeInvoices, fixedFeeInvoicedToDate, auditInvoices,
    refetchBillingData, handleCreateDraft, handleDeleteDraft,
    handlePostToFinancials, handleVoidInvoice,

    // WIP
    wipTotalItems, wipTotalPages, wipStartItem, wipEndItem,
    wipItems, filteredAndSortedWipItems, groupedWipItems, paginatedWipItems,
    selectedWipItems, setSelectedWipItems, selectedWipTotals,
    wipSearchQuery, setWipSearchQuery, wipTypeFilter, setWipTypeFilter,
    wipResourceFilter, setWipResourceFilter, wipGroupBy, setWipGroupBy,
    wipDateFrom, setWipDateFrom, wipDateTo, setWipDateTo,
    wipCurrentPage, setWipCurrentPage, wipItemsPerPage, setWipItemsPerPage,
    wipColumnSorts, setWipColumnSorts,
    handleSelectWipItem, handleSelectAllWipItems, handleWipPageChange, handleWipPageSizeChange, handleRefreshWip,

    // Invoice
    invoiceCurrentPage, setInvoiceCurrentPage,
    invoiceItemsPerPage, setInvoiceItemsPerPage,
    invoiceTotalCount,
    invoiceTotalInvoiced, invoiceTotalInvoicedNet, invoiceTotalOutstanding, invoiceTotalPaid,
    invoicedTotal, invoicedNetTotal,
    invoiceColumnSorts, setInvoiceColumnSorts,
    invoiceStatusFilter, setInvoiceStatusFilter,
    invoiceSearchQuery, setInvoiceSearchQuery,
    handleInvoicePageChange,
    invoiceColumns, updateInvoiceColumns, visibleInvoiceColumns,
    invoiceColumnConfigOpen, setInvoiceColumnConfigOpen,
    wipColumns, updateWipColumns, visibleWipColumns,
    wipColumnConfigOpen, setWipColumnConfigOpen,
    invoiceWizardOpen, setInvoiceWizardOpen,
    editDraftData, setEditDraftData, editDraftLoading, handleEditDraftInvoice,
    fixedFeeMilestone, setFixedFeeMilestone,
    invoiceDetailsSheetOpen, setInvoiceDetailsSheetOpen,
    selectedInvoiceId, setSelectedInvoiceId,
    createDraftDialogOpen, setCreateDraftDialogOpen,

    // Project settings
    projectSettings, setProjectSettings,
    billingSettingsDrawerOpen, setBillingSettingsDrawerOpen,
    drawerEditMode, setDrawerEditMode, drawerSettings, setDrawerSettings, drawerSaving,

    // Budget modals
    salesBudgetEditModalOpen, setSalesBudgetEditModalOpen,
    costBudgetEditModalOpen, setCostBudgetEditModalOpen,
    resourceBudgetEditModalOpen, setResourceBudgetEditModalOpen,
    editSalesBudgetPageOpen, setEditSalesBudgetPageOpen,
    editCostBudgetPageOpen, setEditCostBudgetPageOpen,
    editResourceBudgetPageOpen, setEditResourceBudgetPageOpen,
    budgetExportModalOpen, setBudgetExportModalOpen,
    activeBudgetType, setActiveBudgetType,

    // Revision history
    revisionHistoryOpen, setRevisionHistoryOpen,
    costRevisions, salesRevisions,
    showCostRevisionHistory, setShowCostRevisionHistory,
    costLoadedRevision, setCostLoadedRevision,
    costMakeActiveRevision, setCostMakeActiveRevision,
    costMakeActiveSource, setCostMakeActiveSource,
    isCostNewRevision, setIsCostNewRevision,
    showSalesRevisionHistory, setShowSalesRevisionHistory,
    salesLoadedRevision, setSalesLoadedRevision,
    salesMakeActiveRevision, setSalesMakeActiveRevision,
    salesMakeActiveSource, setSalesMakeActiveSource,
    isSalesNewRevision, setIsSalesNewRevision,
    viewingRevision, setViewingRevision,
    viewingRevisionPageOpen, setViewingRevisionPageOpen,
    newRevisionPageOpen, setNewRevisionPageOpen,
    duplicateRevision, setDuplicateRevision,
    makeActiveRevision, setMakeActiveRevision,
    makeActiveSource, setMakeActiveSource,

    // Cost Centres
    costCentres, setCostCentres,
    costCentresLoading, setCostCentresLoading,
    hierarchicalCostCentres, setHierarchicalCostCentres,
    costCentreColumns, setCostCentreColumns,
    costCentreColumnConfigOpen, setCostCentreColumnConfigOpen,
    isStructureView, setIsStructureView: setIsStructureViewPersisted,
    selectedCostCentre, setSelectedCostCentre,
    costCentreDetailsPanelOpen, setCostCentreDetailsPanelOpen,
    costCentreFullModalOpen, setCostCentreFullModalOpen,
    createCostCentrePanelOpen, setCreateCostCentrePanelOpen,
    configureStructureOpen, setConfigureStructureOpen,
    settingsSaving, defaultCostCentreError, setDefaultCostCentreError,

    // Expenses
    expensesLoading,
    timeEntriesLoading,

    // Completion
    effectiveCompletionPercent, autoCalculatedCompletionPercent,
    completionPercentEditing, setCompletionPercentEditing,
    completionPercentInputValue, setCompletionPercentInputValue,
    handleCompletionPercentEditStart, handleCompletionPercentEditConfirm,
    handleCompletionPercentEditCancel, handleCompletionModeChange,
    completionAuditEntries,

    // Financials
    evmExpanded, setEvmExpanded,
    financialAnalysisExpanded, setFinancialAnalysisExpanded,
    completionAuditOpen, setCompletionAuditOpen,

    // Audit
    auditCategoryFilter, setAuditCategoryFilter, clientAuditEvents,
    invoiceSendAuditEvents, addInvoiceSendAuditEvent,

    // Time
    timeTabPrefilterNames, setTimeTabPrefilterNames,
    timeTabPrefilterBillable, setTimeTabPrefilterBillable,

    // Header editing
    isNameEditing, isDescEditing,
    inlineNameValue, setInlineNameValue,
    inlineDescValue, setInlineDescValue,

    // Project info edit
    isProjectInfoEditMode, setIsProjectInfoEditMode,
    showCancelDialog, setShowCancelDialog,
    hasUnsavedChanges, setHasUnsavedChanges,
    editFormData, setEditFormData,
    handleEnterEditMode, handleCancelEdit, handleConfirmCancel,
    handleSaveProjectInfo: handleSaveChanges,
    handleSaveChanges,

    // Client
    recentClientIds, clientChangeDialogOpen, setClientChangeDialogOpen,
    pendingClientChange, setPendingClientChange, previousClientForDialog,
    isClientChangeLocked, requiresClientChangeConfirmation,
    handleClientChange, handleConfirmClientChange: handleClientChangeConfirm, handleCancelClientChange: handleClientChangeCancel,
    handleClientChangeConfirm, handleClientChangeCancel,

    // Key People
    isKeyPeopleEditMode, setIsKeyPeopleEditMode,
    keyPeopleEditState, setKeyPeopleEditState,
    hasKeyPeopleUnsavedChanges, setHasKeyPeopleUnsavedChanges,
    roleConfigs, getRoleLabel, isRoleEnabled, isRoleRequired,
    recentProjectManagerIds, recentAccountManagerIds,
    handleKeyPeopleEdit: handleEnterKeyPeopleEditMode, handleKeyPeopleSave: handleSaveKeyPeople, handleKeyPeopleCancel: handleCancelKeyPeopleEdit,
    handleEnterKeyPeopleEditMode, handleSaveKeyPeople, handleCancelKeyPeopleEdit, isSavingKeyPeople,

    // PM/AM confirmation
    showPmConfirmDialog, setShowPmConfirmDialog,
    pendingPmChange, setPendingPmChange,
    showAmConfirmDialog, setShowAmConfirmDialog,
    pendingAmChange, setPendingAmChange,
    handlePmChange: handleProjectManagerChange, handleConfirmPmChange, handleCancelPmChange,
    handleProjectManagerChange,
    handleAmChange: handleAccountManagerChange, handleConfirmAmChange, handleCancelAmChange,
    handleAccountManagerChange,

    // Content fields
    contentFieldSettings, customFieldDefs,
    noteLog, setNoteLog, newNoteValue, setNewNoteValue, isSubmittingNote,
    handleAddNote,
    isObjectivesEditMode, setIsObjectivesEditMode, objectivesEditValue, setObjectivesEditValue,
    isDeliverablesEditMode, setIsDeliverablesEditMode, deliverablesEditValue, setDeliverablesEditValue,
    handleSaveObjectives, handleSaveDeliverables,
    isSavingObjectives, isSavingDeliverables,

    // Client Info
    isClientInfoEditMode, setIsClientInfoEditMode,
    clientInfoEditData, setClientInfoEditData,
    hasClientInfoUnsavedChanges, setHasClientInfoUnsavedChanges,
    handleClientInfoEdit: handleEnterClientInfoEditMode, handleSaveClientInfo: handleSaveClientInfoChanges, handleCancelClientInfo: handleCancelClientInfoEdit,
    handleEnterClientInfoEditMode, handleSaveClientInfoChanges, handleCancelClientInfoEdit,

    // Unified card edit (Project Info + Client Info)
    handleEnterUnifiedEditMode, handleCancelUnifiedEdit, handleConfirmCancelUnified,
    handleSaveUnifiedChanges, hasUnifiedUnsavedChanges, isSavingUnified,

    // Project field locks
    isRevRecLocked, wipInvoiceTier, isTypeLocked,
    projectNameHasIssuedInvoices,
    editStartDate, editEndDate, endDateBeforeStart,
    earliestTimeEntryDate, startDateMovedPastTimesheets,
    latestAllocationEndDate, endDateShortenedPastAllocations,
    isSaveBlocked,

    // Handlers
    handleSaveBudget,
    handleDrawerOpen, handleDrawerEdit, handleDrawerCancel, handleDrawerSave,
    handleDrawerSettingsChange,
    handleInlineStatusChange,
    handleNameEditStart, handleNameSave, handleNameKeyDown,
    handleDescEditStart, handleDescSave, handleDescKeyDown,
    handleRemoveTeamMember,
    validateSettings, getNextInvoiceDate, getNextInvoiceDateForDrawer,
    getDefaultBudgetData, getEmployeeName, getStatusBadgeVariant, getInitials,
    formatProjectType, formatRevenueRecognition,
    getStatusColor, handleSaveSettings: handleSettingsChange,
    handleSettingsChange,

    // Computed summaries
    totalBillableHours, totalNonBillableHours, totalExpenseAmount,
    completedMilestones, overdueMilestones, activeResources,
    realisationRatePct,
    pendingChangeRequests, approvedChangeRequests, dataMode,

    // Auth / Company
    currentCompany, invoiceTemplates, getInvoiceTemplate,
    handleApiError,

    // Tab configuration
    allAvailableTabs, selectedTabIds, setSelectedTabIds,
    tabSettingsOpen, setTabSettingsOpen, tabConfigsForDialog,
    visibleTabs, moreTabs, pinnedMoreTab, setPinnedMoreTab,
    saveTabOrder, tabsContainerCallbackRef,
    isStatusSaving, loading,

    // For budget editor pages
    createBudgetRevision,

    // onBack
    onBack,

    // Overview - attributes/objectives/deliverables edit mode
    isAttributesEditMode, setIsAttributesEditMode,
    attributesEditState, setAttributesEditState,
    attributesValidationErrors, setAttributesValidationErrors,
    handleEnterAttributesEditMode, handleCancelAttributesEdit, handleSaveAttributes, isSavingAttributes,
    handleEnterObjectivesEdit, handleCancelObjectivesEdit,
    handleEnterDeliverablesEdit, handleCancelDeliverablesEdit,
    handleFieldChange, handleKeyPeopleRoleChange,

    // Budget restore
    handleRestoreRevision,

    // Scheduling setters (used by resourcing tab)
    setSchedulingBookings, setSchedulingConnectionStatus,
    setSchedulingError, setSchedulingLoading, setSchedulingSchedulerName,
    setSchedulerUrl,
  };
}
